import { relations, sql } from "drizzle-orm";
import {
  type AnyPgColumn,
  boolean,
  date,
  decimal,
  doublePrecision,
  index,
  integer,
  jsonb,
  numeric,
  pgEnum,
  pgTable,
  primaryKey,
  smallint,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ==================== EXPRESS SESSION STORE ====================
export const sessions = pgTable("session", {
  sid: varchar("sid").primaryKey().notNull(),
  sess: jsonb("sess").notNull(),
  expire: timestamp("expire", { precision: 6 }).notNull(),
});

export const insertSessionSchema = createInsertSchema(sessions);
export type Session = typeof sessions.$inferSelect;

// ==================== USERS (Authentication) ====================
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").unique().notNull(),
  clerkId: text("clerk_id").unique(),
  isSuperAdmin: boolean("is_super_admin").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const usersRelations = relations(users, ({ one }) => ({
  employee: one(employees),
}));

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// ==================== EMPLOYEE (Core - Source of Truth) ====================
export const employees = pgTable(
  "employees",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").references(() => users.id),
    fullName: text("full_name").notNull(),
    personalEmail: text("personal_email"),
    corporateEmail: text("corporate_email").unique(),
    phone: text("phone"),
    cpf: text("cpf").unique(),
    birthdate: date("birthdate"),
    address: text("address"),
    emergencyContact: jsonb("emergency_contact"),
    employmentType: text("employment_type"), // PJ | CLT
    status: text("status").notNull().default("active"), // onboarding | active | inactive
    startDate: date("start_date"),
    endDate: date("end_date"),
    jobTitle: text("job_title"),
    compDiscipline: text("comp_discipline"),
    jobLevel: text("job_level"), // L0-L9 IC/MGMT
    department: text("department"),
    directManagerId: varchar("direct_manager_id").references(() => employees.id, {
      onDelete: "set null",
    }),
    workType: text("work_type"), // remote | hybrid | onsite
    currentSalary: decimal("current_salary", { precision: 12, scale: 2 }),
    currency: text("currency").notNull().default("BRL"), // BRL | USD
    salaryTableId: varchar("salary_table_id"),
    sop: text("sop"),
    slackId: text("slack_id").unique(), // Slack user ID for bot integration
    slackPhotoUrl: text("slack_photo_url"),
    slackPhotoUpdatedAt: timestamp("slack_photo_updated_at"),
    buddyEligible: boolean("buddy_eligible").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [index("idx_employees_status_start_date").on(table.status, table.startDate)],
);

export const employeesRelations = relations(employees, ({ one, many }) => ({
  user: one(users, {
    fields: [employees.userId],
    references: [users.id],
  }),
  directManager: one(employees, {
    fields: [employees.directManagerId],
    references: [employees.id],
    relationName: "managerEmployee",
  }),
  directReports: many(employees, { relationName: "managerEmployee" }),
  schedulingLinks: many(employeeSchedulingLinks),
  compensationEntries: many(compensationEntries),
  performanceCycles: many(performanceCycles),
  jobFavorites: many(jobFavorites),
  salaryTable: one(salaryTables, {
    fields: [employees.salaryTableId],
    references: [salaryTables.id],
  }),
}));

export const insertEmployeeSchema = createInsertSchema(employees).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEmployee = z.infer<typeof insertEmployeeSchema>;
export type Employee = typeof employees.$inferSelect;

// ==================== EMPLOYEE SCHEDULING LINKS ====================
export const employeeSchedulingLinks = pgTable(
  "employee_scheduling_links",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    url: text("url").notNull(),
    durationMinutes: integer("duration_minutes"), // null = default/any duration
    isDefault: boolean("is_default").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [index("idx_employee_scheduling_links_employee_id").on(table.employeeId)],
);

export const employeeSchedulingLinksRelations = relations(employeeSchedulingLinks, ({ one }) => ({
  employee: one(employees, {
    fields: [employeeSchedulingLinks.employeeId],
    references: [employees.id],
  }),
}));

export const insertEmployeeSchedulingLinkSchema = createInsertSchema(employeeSchedulingLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertEmployeeSchedulingLink = z.infer<typeof insertEmployeeSchedulingLinkSchema>;
export type EmployeeSchedulingLink = typeof employeeSchedulingLinks.$inferSelect;

// Domain types for Employee with scheduling links
export type SchedulingLink = {
  id: string;
  url: string;
  durationMinutes: number | null;
  isDefault: boolean;
};

export type EmployeeWithSchedulingLinks = Employee & {
  schedulingLinks: SchedulingLink[];
};

// Slim DTO for the stage-interviewers picker. Mirrors the `dashboard.people`
// shape: only the fields the picker needs, never the full employee row (no
// CPF, salary, sop, address, etc.). Carries `schedulingLinks` (the array from
// employee_scheduling_links) so the picker resolves the link from the right
// source — NOT the deprecated singular `employees.scheduling_link`.
export type StageInterviewerDTO = {
  id: string;
  fullName: string;
  corporateEmail: string | null;
  schedulingLinks: SchedulingLink[];
  interviewCount: number;
};

// Group members don't carry their own scheduling links — the group uses its
// own `schedulingLink`. Slim them down to just what the picker label needs.
export type StageInterviewerMemberDTO = {
  id: string;
  fullName: string;
  corporateEmail: string | null;
  interviewCount: number;
};

export type StageInterviewerGroupDTO = {
  id: string;
  schedulingLink: string | null;
  members: StageInterviewerMemberDTO[];
};

// One interview stage with its interviewers. The endpoint returns an array of
// these (one per stage): StageInterviewersDTO[].
export type StageInterviewersDTO = {
  stageId: string;
  employees: StageInterviewerDTO[];
  groups: StageInterviewerGroupDTO[];
};

/**
 * Resolve the best scheduling link for a given duration.
 * 1. Exact match on durationMinutes
 * 2. Fallback to isDefault=true
 * 3. Fallback to first link
 * Returns null if no links exist.
 */
export function resolveSchedulingLink(
  links: SchedulingLink[],
  durationMinutes?: number | null,
): string | null {
  if (links.length === 0) return null;
  if (durationMinutes) {
    const match = links.find((l) => l.durationMinutes === durationMinutes);
    if (match) return match.url;
  }
  const defaultLink = links.find((l) => l.isDefault);
  return defaultLink?.url ?? links[0]?.url ?? null;
}

// ==================== COMPENSATION ENTRY ====================
export const compensationEntries = pgTable("compensation_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  employeeId: varchar("employee_id")
    .references(() => employees.id, { onDelete: "cascade" })
    .notNull(),
  type: text("type").notNull(), // initial | salary_raise | bonus | commission
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const compensationEntriesRelations = relations(compensationEntries, ({ one }) => ({
  employee: one(employees, {
    fields: [compensationEntries.employeeId],
    references: [employees.id],
  }),
}));

export const insertCompensationEntrySchema = createInsertSchema(compensationEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCompensationEntry = z.infer<typeof insertCompensationEntrySchema>;
export type CompensationEntry = typeof compensationEntries.$inferSelect;

// ==================== ATS - APPLICANT ====================
export const applicants = pgTable("applicants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fullName: text("full_name").notNull(),
  email: text("email").unique(),
  phone: text("phone"),
  linkedinUrl: text("linkedin_url"),
  employeeId: varchar("employee_id").references(() => employees.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const applicantsRelations = relations(applicants, ({ one, many }) => ({
  employee: one(employees, {
    fields: [applicants.employeeId],
    references: [employees.id],
  }),
  applications: many(applications),
}));

export const insertApplicantSchema = createInsertSchema(applicants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertApplicant = z.infer<typeof insertApplicantSchema>;
export type Applicant = typeof applicants.$inferSelect;

// ==================== OFFER APPROVAL RULES ====================
export type ConditionField =
  | "salary_base"
  | "comping_ratio"
  | "sop_ratio"
  | "signing_bonus"
  | "performance_bonus"
  | "equity_shares"
  | "performance_bonus_shares"
  | "job_level";

export type ConditionOperator = "gt" | "gte" | "lt" | "lte" | "eq" | "is_present" | "not_present";

export interface Condition {
  type: "condition";
  field: ConditionField;
  operator: ConditionOperator;
  value?: number | string;
}

export interface RuleGroup {
  type: "group";
  logic: "any" | "all";
  nodes: Array<Condition | RuleGroup>;
}

export type OfferApprovalRules = RuleGroup;

export const conditionFieldSchema = z.enum([
  "salary_base",
  "comping_ratio",
  "sop_ratio",
  "signing_bonus",
  "performance_bonus",
  "equity_shares",
  "performance_bonus_shares",
  "job_level",
]);

export const conditionOperatorSchema = z.enum([
  "gt",
  "gte",
  "lt",
  "lte",
  "eq",
  "is_present",
  "not_present",
]);

export const conditionSchema: z.ZodType<Condition> = z.object({
  type: z.literal("condition"),
  field: conditionFieldSchema,
  operator: conditionOperatorSchema,
  value: z.union([z.number(), z.string()]).optional(),
});

export const ruleGroupSchema: z.ZodType<RuleGroup> = z.lazy(() =>
  z.object({
    type: z.literal("group"),
    logic: z.enum(["any", "all"]),
    nodes: z.array(z.union([conditionSchema, ruleGroupSchema])),
  }),
);

export const offerApprovalRulesSchema: z.ZodType<OfferApprovalRules> = ruleGroupSchema;

// ==================== ATS - JOB OPENING ====================
export const jobOpenings = pgTable("job_openings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  generalResponsibilities: text("general_responsibilities").notNull(),
  desiredProfile: text("desired_profile").notNull(),
  compensation: text("compensation").notNull(),
  jobLevel: text("job_level").notNull(), // L0-L7
  department: text("department").notNull(),
  hiringManagerId: varchar("hiring_manager_id").references(() => employees.id, {
    onDelete: "set null",
  }),
  salaryExpected: decimal("salary_expected", { precision: 12, scale: 2 }).notNull(),
  status: text("status").notNull().default("pending_approval"), // pending_approval | open | closed | rejected
  approvedBy: varchar("approved_by").references(() => employees.id, { onDelete: "set null" }),
  approvedAt: timestamp("approved_at"),
  rejectionReason: text("rejection_reason"),
  hiddenFromCareers: boolean("hidden_from_careers").notNull().default(false),
  allowAutoAdvance: boolean("allow_auto_advance").notNull().default(false),
  notifyHmOnFeedback: boolean("notify_hm_on_feedback").notNull().default(true),
  notifyHmDailyDigest: boolean("notify_hm_daily_digest").notNull().default(true),
  notifyHmOnNewApplication: boolean("notify_hm_on_new_application").notNull().default(false),
  offerApprovalRules: jsonb("offer_approval_rules").$type<OfferApprovalRules>(),
  createdBy: varchar("created_by").references(() => employees.id, { onDelete: "set null" }),
  notionId: varchar("notion_id", { length: 255 }).unique(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const jobOpeningsRelations = relations(jobOpenings, ({ one, many }) => ({
  hiringManager: one(employees, {
    fields: [jobOpenings.hiringManagerId],
    references: [employees.id],
    relationName: "hiringManagerJob",
  }),
  approver: one(employees, {
    fields: [jobOpenings.approvedBy],
    references: [employees.id],
    relationName: "approverJob",
  }),
  creator: one(employees, {
    fields: [jobOpenings.createdBy],
    references: [employees.id],
    relationName: "creatorJob",
  }),
  interviewStages: many(interviewStages),
  applications: many(applications),
  propertyDefinitions: many(jobOpeningPropertyDefinitions),
  views: many(jobOpeningViews),
  favorites: many(jobFavorites),
  digestSchedule: many(jobDigestSchedule),
}));

// ==================== ATS - JOB DIGEST SCHEDULE ====================
export const jobDigestSchedule = pgTable(
  "job_digest_schedule",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    jobOpeningId: varchar("job_opening_id")
      .references(() => jobOpenings.id, { onDelete: "cascade" })
      .notNull(),
    hour: smallint("hour").notNull(), // 0–23 BRT
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_job_digest_schedule_job_hour").on(table.jobOpeningId, table.hour),
    index("idx_job_digest_schedule_hour").on(table.hour),
  ],
);

export const jobDigestScheduleRelations = relations(jobDigestSchedule, ({ one }) => ({
  jobOpening: one(jobOpenings, {
    fields: [jobDigestSchedule.jobOpeningId],
    references: [jobOpenings.id],
  }),
}));

export type JobDigestSchedule = typeof jobDigestSchedule.$inferSelect;

export const insertJobDigestScheduleSchema = createInsertSchema(jobDigestSchedule).omit({
  id: true,
});

export type InsertJobDigestSchedule = z.infer<typeof insertJobDigestScheduleSchema>;

export const insertJobOpeningSchema = createInsertSchema(jobOpenings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  approvedBy: true,
  approvedAt: true,
  rejectionReason: true,
});

export type InsertJobOpening = z.infer<typeof insertJobOpeningSchema>;
export type JobOpening = typeof jobOpenings.$inferSelect;

// ==================== ATS - JOB FEEDBACK FORMS ====================
// Named feedback form templates, owned by a job opening. The HM builds forms
// in the job settings and each interview stage picks one (or the default Comp
// form). When a stage uses a custom form, its criteria replace the default
// Comp trio (GCA / Role Knowledge / COMP Way); decision, general comments,
// excitement score and notes-for-next-interviewer are always present.
export const stageFeedbackCriterionSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().trim().min(1).max(120),
    description: z.string().max(500).optional(),
    scaleType: z.enum(["binary", "numeric"]),
    // Bounds of the numeric scale (score goes scaleMin..scaleMax). Ignored for
    // binary. Range capped at 10 points so score pickers stay usable (web
    // buttons / Slack select options).
    scaleMin: z.number().int().min(0).max(99).optional(),
    scaleMax: z.number().int().min(1).max(100).optional(),
    // When true, this criterion's comment is mandatory on submission.
    requireNotes: z.boolean().optional(),
  })
  .refine((c) => c.scaleType === "binary" || c.scaleMax !== undefined, {
    message: "Máximo é obrigatório para escala de nota",
    path: ["scaleMax"],
  })
  .refine((c) => c.scaleType === "binary" || (c.scaleMin ?? 0) < (c.scaleMax ?? 0), {
    message: "Mínimo deve ser menor que o máximo",
    path: ["scaleMin"],
  })
  .refine((c) => c.scaleType === "binary" || (c.scaleMax ?? 0) - (c.scaleMin ?? 0) <= 10, {
    message: "A escala pode ter no máximo 10 pontos de amplitude",
    path: ["scaleMax"],
  });

export type StageFeedbackCriterion = z.infer<typeof stageFeedbackCriterionSchema>;

export const stageFeedbackCriteriaSchema = z
  .array(stageFeedbackCriterionSchema)
  .max(15)
  .refine((list) => new Set(list.map((c) => c.id)).size === list.length, {
    message: "Critérios com id duplicado",
  });

// One submitted score per custom criterion. The criterion definition (label,
// scale) is snapshotted at submission time so feedback never references a
// live definition that may change later.
export const customCriterionScoreSchema = z.object({
  criterionId: z.string().min(1),
  label: z.string().min(1),
  scaleType: z.enum(["binary", "numeric"]),
  scaleMin: z.number().int().optional(),
  scaleMax: z.number().int().optional(),
  // Snapshot of the criterion's comment-required flag, so edits enforce the
  // rule as it was when the feedback was first submitted.
  requireNotes: z.boolean().optional(),
  // binary: 0 = não, 1 = sim; numeric: scaleMin..scaleMax
  score: z.number().int().min(0),
  notes: z.string().optional(),
});

export type CustomCriterionScore = z.infer<typeof customCriterionScoreSchema>;

export const jobFeedbackForms = pgTable("job_feedback_forms", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobOpeningId: varchar("job_opening_id")
    .references(() => jobOpenings.id, { onDelete: "cascade" })
    .notNull(),
  name: text("name").notNull(),
  criteria: jsonb("criteria").$type<StageFeedbackCriterion[]>().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const jobFeedbackFormsRelations = relations(jobFeedbackForms, ({ one }) => ({
  jobOpening: one(jobOpenings, {
    fields: [jobFeedbackForms.jobOpeningId],
    references: [jobOpenings.id],
  }),
}));

export const insertJobFeedbackFormSchema = createInsertSchema(jobFeedbackForms).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertJobFeedbackForm = z.infer<typeof insertJobFeedbackFormSchema>;
export type JobFeedbackForm = typeof jobFeedbackForms.$inferSelect;

export const interviewStages = pgTable("interview_stages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobOpeningId: varchar("job_opening_id")
    .references(() => jobOpenings.id)
    .notNull(),
  stageName: text("stage_name").notNull(), // Screening, Technical, Cultural Fit, Founder
  stageOrder: integer("stage_order").notNull(),
  durationMinutes: integer("duration_minutes").notNull(),
  defaultInterviewerId: varchar("default_interviewer_id").references(() => employees.id, {
    onDelete: "set null",
  }),
  hidden: boolean("hidden").notNull().default(false),
  materials: text("materials"),
  description: text("description"),
  // Feedback form used by this stage. null = default Comp form.
  feedbackFormId: varchar("feedback_form_id").references(() => jobFeedbackForms.id, {
    onDelete: "set null",
  }),
  archivedAt: timestamp("archived_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const interviewStagesRelations = relations(interviewStages, ({ one, many }) => ({
  jobOpening: one(jobOpenings, {
    fields: [interviewStages.jobOpeningId],
    references: [jobOpenings.id],
  }),
  defaultInterviewer: one(employees, {
    fields: [interviewStages.defaultInterviewerId],
    references: [employees.id],
  }),
  interviews: many(interviews),
  stageInterviewers: many(interviewStageInterviewers),
  interviewerGroups: many(interviewerGroups),
}));

export const insertInterviewStageSchema = createInsertSchema(interviewStages).omit({
  id: true,
  createdAt: true,
});

export type InsertInterviewStage = z.infer<typeof insertInterviewStageSchema>;
export type InterviewStage = typeof interviewStages.$inferSelect;

// ==================== ATS - INTERVIEWER GROUPS ====================
export const interviewerGroups = pgTable("interviewer_groups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  interviewStageId: varchar("interview_stage_id")
    .references(() => interviewStages.id, { onDelete: "cascade" })
    .notNull(),
  schedulingLink: text("scheduling_link"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const interviewerGroupsRelations = relations(interviewerGroups, ({ one, many }) => ({
  interviewStage: one(interviewStages, {
    fields: [interviewerGroups.interviewStageId],
    references: [interviewStages.id],
  }),
  members: many(interviewStageInterviewers),
}));

export const insertInterviewerGroupSchema = createInsertSchema(interviewerGroups).omit({
  id: true,
  createdAt: true,
});

export type InsertInterviewerGroup = z.infer<typeof insertInterviewerGroupSchema>;
export type InterviewerGroup = typeof interviewerGroups.$inferSelect;

// ==================== ATS - INTERVIEW STAGE INTERVIEWERS (N:M) ====================
export const interviewStageInterviewers = pgTable(
  "interview_stage_interviewers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    interviewStageId: varchar("interview_stage_id")
      .references(() => interviewStages.id, { onDelete: "cascade" })
      .notNull(),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    groupId: varchar("group_id").references(() => interviewerGroups.id),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueStageEmployeeGroup: unique().on(table.interviewStageId, table.employeeId, table.groupId),
  }),
);

export const interviewStageInterviewersRelations = relations(
  interviewStageInterviewers,
  ({ one }) => ({
    interviewStage: one(interviewStages, {
      fields: [interviewStageInterviewers.interviewStageId],
      references: [interviewStages.id],
    }),
    employee: one(employees, {
      fields: [interviewStageInterviewers.employeeId],
      references: [employees.id],
    }),
    group: one(interviewerGroups, {
      fields: [interviewStageInterviewers.groupId],
      references: [interviewerGroups.id],
    }),
  }),
);

export const insertInterviewStageInterviewerSchema = createInsertSchema(
  interviewStageInterviewers,
).omit({
  id: true,
  createdAt: true,
});

export type InsertInterviewStageInterviewer = z.infer<typeof insertInterviewStageInterviewerSchema>;
export type InterviewStageInterviewer = typeof interviewStageInterviewers.$inferSelect;

// ==================== ATS - APPLICATION ====================
// Kanban columns: Inscrito | Priorizado | Despriorizado | (Etapas de entrevista) | Reference Checks | Dossiê | Oferta | Contratado | Rejeitado | Offer Rejected
// Status: applied, prioritized, deprioritized, interviewing, reference_checks, dossier, offer, hired, rejected, offer_rejected

// Application status enum
export const APPLICATION_STATUSES = [
  "applied",
  "prioritized",
  "deprioritized",
  "interviewing",
  "reference_checks",
  "dossier",
  "offer",
  "hired",
  "rejected",
  "offer_rejected",
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];
export const applicationStatusSchema = z.enum(APPLICATION_STATUSES);

// Dossier decision enum
export const DOSSIER_DECISIONS = ["approved", "rejected"] as const;
export type DossierDecision = (typeof DOSSIER_DECISIONS)[number];
export const dossierDecisionSchema = z.enum(DOSSIER_DECISIONS);

export const applications = pgTable("applications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobOpeningId: varchar("job_opening_id")
    .references(() => jobOpenings.id)
    .notNull(),
  applicantId: varchar("applicant_id")
    .references(() => applicants.id)
    .notNull(),
  type: text("type"), // organic | referral | hunting_comp | consultoria
  resumePath: text("resume_path"),
  linkedinUrl: text("linkedin_url"),
  coverLetter: text("cover_letter"),
  aiVettingScore: integer("ai_vetting_score"),
  aiVettingNotes: text("ai_vetting_notes"),
  aiScreeningResult: jsonb("ai_screening_result"),
  status: text("status").notNull().default("applied"),
  currentInterviewStageId: varchar("current_interview_stage_id").references(
    () => interviewStages.id,
  ),
  currentStepOwnerId: varchar("current_step_owner_id").references(() => employees.id, {
    onDelete: "set null",
  }),
  rejectionReason: text("rejection_reason"),
  rejectionFeedback: text("rejection_feedback"),
  rejectionFeedbackSentAt: timestamp("rejection_feedback_sent_at"),
  dossierDecision: text("dossier_decision"),
  dossierDecisionReason: text("dossier_decision_reason"),
  dossierDecidedByEmployeeId: varchar("dossier_decided_by_employee_id").references(
    () => employees.id,
    { onDelete: "set null" },
  ),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const applicationsRelations = relations(applications, ({ one, many }) => ({
  jobOpening: one(jobOpenings, {
    fields: [applications.jobOpeningId],
    references: [jobOpenings.id],
  }),
  applicant: one(applicants, {
    fields: [applications.applicantId],
    references: [applicants.id],
  }),
  currentInterviewStage: one(interviewStages, {
    fields: [applications.currentInterviewStageId],
    references: [interviewStages.id],
  }),
  currentStepOwner: one(employees, {
    fields: [applications.currentStepOwnerId],
    references: [employees.id],
    relationName: "applicationOwner",
  }),
  dossierDecidedByEmployee: one(employees, {
    fields: [applications.dossierDecidedByEmployeeId],
    references: [employees.id],
    relationName: "dossierDecider",
  }),
  interviews: many(interviews),
  offers: many(offers),
  offerRecommendations: many(offerRecommendations),
  excitementScores: many(excitementScores),
  referenceChecks: many(referenceChecks),
  propertyValues: many(applicationPropertyValues),
}));

export const insertApplicationSchema = createInsertSchema(applications)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    status: applicationStatusSchema.default("applied"),
  });

export type InsertApplication = z.infer<typeof insertApplicationSchema>;
export type Application = typeof applications.$inferSelect;

// ==================== ATS - CUSTOM PROPERTY DEFINITIONS ====================
export const propertyType = pgEnum("property_type", ["text", "select", "multi_select"]);

export const jobOpeningPropertyDefinitions = pgTable("job_opening_property_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobOpeningId: varchar("job_opening_id")
    .notNull()
    .references(() => jobOpenings.id, { onDelete: "cascade" }),
  name: text("name").notNull().default("Nova propriedade"),
  type: propertyType("type").notNull().default("text"),
  options: jsonb("options")
    .$type<Array<{ id: string; label: string; color: string }>>()
    .notNull()
    .default([]),
  showOnCard: boolean("show_on_card").notNull().default(false),
  order: integer("order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const applicationPropertyValues = pgTable(
  "application_property_values",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .notNull()
      .references(() => applications.id, { onDelete: "cascade" }),
    definitionId: varchar("definition_id")
      .notNull()
      .references(() => jobOpeningPropertyDefinitions.id, {
        onDelete: "cascade",
      }),
    value: jsonb("value").$type<string | string[]>(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    uniqueApplicationDefinition: unique().on(table.applicationId, table.definitionId),
  }),
);

export const insertPropertyDefinitionSchema = createInsertSchema(
  jobOpeningPropertyDefinitions,
).omit({ id: true, createdAt: true });

export const insertPropertyValueSchema = createInsertSchema(applicationPropertyValues).omit({
  id: true,
  updatedAt: true,
});

export type PropertyDefinition = typeof jobOpeningPropertyDefinitions.$inferSelect;
export type InsertPropertyDefinition = z.infer<typeof insertPropertyDefinitionSchema>;
export type PropertyValue = typeof applicationPropertyValues.$inferSelect;
export type InsertPropertyValue = z.infer<typeof insertPropertyValueSchema>;

export const jobOpeningPropertyDefinitionsRelations = relations(
  jobOpeningPropertyDefinitions,
  ({ one, many }) => ({
    jobOpening: one(jobOpenings, {
      fields: [jobOpeningPropertyDefinitions.jobOpeningId],
      references: [jobOpenings.id],
    }),
    values: many(applicationPropertyValues),
  }),
);

export const applicationPropertyValuesRelations = relations(
  applicationPropertyValues,
  ({ one }) => ({
    application: one(applications, {
      fields: [applicationPropertyValues.applicationId],
      references: [applications.id],
    }),
    definition: one(jobOpeningPropertyDefinitions, {
      fields: [applicationPropertyValues.definitionId],
      references: [jobOpeningPropertyDefinitions.id],
    }),
  }),
);

// ==================== ATS - REFERENCE CHECKS ====================

export const referenceChecks = pgTable("reference_checks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  applicationId: varchar("application_id")
    .references(() => applications.id, { onDelete: "cascade" })
    .notNull(),
  referrerName: text("referrer_name").notNull(),
  referrerLinkedin: text("referrer_linkedin"),
  referrerPhone: text("referrer_phone"),
  answers: jsonb("answers").$type<{ question: string; answer: string }[]>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const referenceChecksRelations = relations(referenceChecks, ({ one }) => ({
  application: one(applications, {
    fields: [referenceChecks.applicationId],
    references: [applications.id],
  }),
}));

export const insertReferenceCheckSchema = createInsertSchema(referenceChecks, {
  answers: z
    .array(z.object({ question: z.string().min(1), answer: z.string() }))
    .nullable()
    .optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export type ReferenceCheck = typeof referenceChecks.$inferSelect;
export type InsertReferenceCheck = typeof referenceChecks.$inferInsert;

// ==================== ATS - APPLICATION HISTORY ====================
// Tracks all state transitions for applications
export const HISTORY_CHANGED_BY = ["SYSTEM", "USER"] as const;
export type HistoryChangedBy = (typeof HISTORY_CHANGED_BY)[number];
export const historyChangedBySchema = z.enum(HISTORY_CHANGED_BY);

// Discriminates what KIND of event a history row represents. Only `stage_move`
// is an actual pipeline column change (oldStatus → newStatus). The others are
// interview-scoped events that do NOT move the candidate's status — the UI must
// render them with their own sentence instead of a "etapa Y → Z" arrow.
export const APPLICATION_HISTORY_EVENT_TYPES = [
  "stage_move",
  "interview_created",
  "interview_stage_changed",
  "interviewer_changed",
] as const;
export type ApplicationHistoryEventType = (typeof APPLICATION_HISTORY_EVENT_TYPES)[number];

// Stable keys identifying which automated flow produced a SYSTEM event. Resolved
// to a human label in the UI. Null for USER events.
export const APPLICATION_HISTORY_SYSTEM_SOURCES = ["ai_screening", "auto_advance"] as const;
export type ApplicationHistorySystemSource = (typeof APPLICATION_HISTORY_SYSTEM_SOURCES)[number];

export const applicationHistory = pgTable("application_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  applicationId: varchar("application_id")
    .references(() => applications.id)
    .notNull(),
  oldStatus: text("old_status"), // null for initial entry
  newStatus: text("new_status").notNull(),
  oldStepName: text("old_step_name"), // nullable - display name for previous step (e.g., interview stage name)
  newStepName: text("new_step_name"), // display name for new step (e.g., interview stage name)
  interviewId: varchar("interview_id").references(() => interviews.id),
  // Event discriminator — see APPLICATION_HISTORY_EVENT_TYPES. Defaults to
  // stage_move so legacy rows keep the previous "status change" semantics.
  eventType: text("event_type").notNull().default("stage_move"),
  changedBy: text("changed_by").notNull().default("USER"), // SYSTEM | USER
  changedByUserId: varchar("changed_by_user_id").references(() => users.id),
  // For SYSTEM events: which automation did it (see APPLICATION_HISTORY_SYSTEM_SOURCES).
  systemSource: text("system_source"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

export const applicationHistoryRelations = relations(applicationHistory, ({ one }) => ({
  application: one(applications, {
    fields: [applicationHistory.applicationId],
    references: [applications.id],
  }),
  interview: one(interviews, {
    fields: [applicationHistory.interviewId],
    references: [interviews.id],
  }),
  changedByUser: one(users, {
    fields: [applicationHistory.changedByUserId],
    references: [users.id],
  }),
}));

export const insertApplicationHistorySchema = createInsertSchema(applicationHistory).omit({
  id: true,
  changedAt: true,
});

export type InsertApplicationHistory = z.infer<typeof insertApplicationHistorySchema>;
export type ApplicationHistory = typeof applicationHistory.$inferSelect;

// ==================== ATS - INTERVIEW ====================
export const interviews = pgTable(
  "interviews",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .references(() => applications.id)
      .notNull(),
    interviewStageId: varchar("interview_stage_id")
      .references(() => interviewStages.id, { onDelete: "cascade" })
      .notNull(),
    interviewerId: varchar("interviewer_id").references(() => employees.id, {
      onDelete: "set null",
    }),
    interviewerGroupId: varchar("interviewer_group_id").references(() => interviewerGroups.id, {
      onDelete: "set null",
    }),
    status: text("status").notNull().default("pending"), // pending | scheduled | finished
    scheduledAt: timestamp("scheduled_at"),
    meetingLink: text("meeting_link"),
    calendarIcsSent: boolean("calendar_ics_sent").default(false),
    completedAt: timestamp("completed_at"),
    rating: integer("rating"),
    gcaScore: integer("gca_score"),
    gcaNotes: text("gca_notes"),
    gcaVerdict: text("gca_verdict"), // YES | NO | UNKNOWN
    roleKnowledgeScore: integer("role_knowledge_score"),
    roleKnowledgeNotes: text("role_knowledge_notes"),
    roleKnowledgeVerdict: text("role_knowledge_verdict"), // YES | NO | UNKNOWN
    compWayScore: integer("comp_way_score"),
    compWayNotes: text("comp_way_notes"),
    compWayVerdict: text("comp_way_verdict"), // YES | NO | UNKNOWN
    // Interviewer's own reading of how excited the candidate is about Comp.
    // Captured during feedback submission. Takes precedence over the IA-inferred
    // score in `candidate_excitement_scores` when present; the IA value stays
    // there for calibration (compare what the human in the room saw vs what
    // the transcript extracted).
    candidateExcitementScore: integer("candidate_excitement_score"), // 0..10
    candidateExcitementNote: text("candidate_excitement_note"),
    contextSignals: text("context_signals"),
    generalComments: text("general_comments"),
    notesForNextInterviewer: text("notes_for_next_interviewer"),
    firefliesRecordingUrl: text("fireflies_recording_url"),
    transcriptText: text("transcript_text"),
    aiDraftUsed: boolean("ai_draft_used").notNull().default(false),
    aiDraftFeedback: text("ai_draft_feedback"), // good | bad | null — legacy, last-click-wins global
    // Cached LLM draft so re-opening the dialog doesn't re-run the model. Keyed
    // by `aiDraftPromptVersion` against the published `interview-draft:generate`
    // skill version — on mismatch we regenerate and overwrite.
    aiDraftJson: jsonb("ai_draft_json"),
    aiDraftPromptVersion: integer("ai_draft_prompt_version"),
    // Granular signal for the AI improvement loop. Capture pair:
    //   - `feedback`: per-field thumbs (good|bad|null) — what the manager rated
    //   - `edited`:   per-field bool — whether the manager touched that field
    // Shape: { feedback: { gca, role, comp, general, next }, edited: { gca, role, comp, general, next, decision } }
    // Original AI draft lives in `aiDraftJson`; final submitted values live in the
    // dedicated columns (gcaNotes/roleKnowledgeNotes/compWayNotes/generalComments/
    // notesForNextInterviewer/recommendation). Together they let us reconstruct
    // (a) which fields the model nailed (no edit), (b) which it missed
    // (edited or rated bad), (c) the diff between draft and final.
    aiDraftSignal: jsonb("ai_draft_signal"),
    // FBK-INPUT-MODE-V2: free-text interviewer impressions the AI structured the
    // draft from (with_inputs mode), and which generation mode produced the draft.
    interviewerInputs: text("interviewer_inputs"),
    draftMode: text("draft_mode"), // transcript_only | with_inputs | manual | null
    // Scores for the stage's custom feedback form (definitions snapshotted at
    // submission). Present only when the stage references a feedback form; in
    // that case the default gca/roleKnowledge/compWay fields stay null.
    customCriteriaScores: jsonb("custom_criteria_scores").$type<CustomCriterionScore[] | null>(),
    recommendation: text("recommendation"), // advance | reject | unsure
    filledByEmployeeId: varchar("filled_by_employee_id").references(() => employees.id, {
      onDelete: "set null",
    }),
    googleCalendarEventId: varchar("google_calendar_event_id"),
    googleCalendarEventUpdated: varchar("google_calendar_event_updated", { length: 50 }),
    scheduledEndAt: timestamp("scheduled_end_at"),
    reminderSent: boolean("reminder_sent").notNull().default(false),
    candidateNotifiedAt: timestamp("candidate_notified_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    idxInterviewReminderLookup: index("idx_interviews_status_reminder_scheduled").on(
      table.status,
      table.reminderSent,
      table.scheduledAt,
    ),
    idxInterviewFinishChecker: index("idx_interviews_status_scheduled_end").on(
      table.status,
      table.scheduledEndAt,
    ),
  }),
);

export const interviewsRelations = relations(interviews, ({ one }) => ({
  application: one(applications, {
    fields: [interviews.applicationId],
    references: [applications.id],
  }),
  interviewStage: one(interviewStages, {
    fields: [interviews.interviewStageId],
    references: [interviewStages.id],
  }),
  interviewer: one(employees, {
    fields: [interviews.interviewerId],
    references: [employees.id],
  }),
  filledBy: one(employees, {
    fields: [interviews.filledByEmployeeId],
    references: [employees.id],
    relationName: "interviewFilledBy",
  }),
}));

export const insertInterviewSchema = createInsertSchema(interviews).omit({
  id: true,
  createdAt: true,
});

export type InsertInterview = z.infer<typeof insertInterviewSchema>;
export type Interview = typeof interviews.$inferSelect;

/** API response for interview creation when email send is attempted */
export type CreateInterviewResponse = Interview & { emailFailed?: boolean };

/** API response for feedback submission when auto-advance email fails */
export type SubmitFeedbackResponse = Interview & { advanceEmailFailed?: boolean };

// ==================== ATS - INTERVIEW FEEDBACK (Group) ====================
export const interviewFeedbacks = pgTable(
  "interview_feedbacks",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    interviewId: varchar("interview_id")
      .references(() => interviews.id, { onDelete: "cascade" })
      .notNull(),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    gcaScore: integer("gca_score"),
    gcaNotes: text("gca_notes"),
    gcaVerdict: text("gca_verdict"), // YES | NO | UNKNOWN
    roleKnowledgeScore: integer("role_knowledge_score"),
    roleKnowledgeNotes: text("role_knowledge_notes"),
    roleKnowledgeVerdict: text("role_knowledge_verdict"), // YES | NO | UNKNOWN
    compWayScore: integer("comp_way_score"),
    compWayNotes: text("comp_way_notes"),
    compWayVerdict: text("comp_way_verdict"), // YES | NO | UNKNOWN
    // Per-feedback reading of the candidate's excitement about Comp.
    // For group interviews, each member submits their own; the aggregate
    // travels to the interview row when feedbacks are merged.
    candidateExcitementScore: integer("candidate_excitement_score"), // 0..10
    candidateExcitementNote: text("candidate_excitement_note"),
    contextSignals: text("context_signals"),
    generalComments: text("general_comments"),
    notesForNextInterviewer: text("notes_for_next_interviewer"),
    // Per-member scores for the stage's custom feedback form (snapshot).
    customCriteriaScores: jsonb("custom_criteria_scores").$type<CustomCriterionScore[] | null>(),
    recommendation: text("recommendation"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("interview_feedbacks_interview_employee_idx").on(
      table.interviewId,
      table.employeeId,
    ),
  ],
);

export const interviewFeedbacksRelations = relations(interviewFeedbacks, ({ one }) => ({
  interview: one(interviews, {
    fields: [interviewFeedbacks.interviewId],
    references: [interviews.id],
  }),
  employee: one(employees, {
    fields: [interviewFeedbacks.employeeId],
    references: [employees.id],
  }),
}));

export const insertInterviewFeedbackSchema = createInsertSchema(interviewFeedbacks).omit({
  id: true,
  createdAt: true,
});

export type InsertInterviewFeedback = z.infer<typeof insertInterviewFeedbackSchema>;
export type InterviewFeedback = typeof interviewFeedbacks.$inferSelect;

// ==================== ATS - OFFER ====================
export const offers = pgTable("offers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  applicationId: varchar("application_id")
    .references(() => applications.id)
    .notNull(),
  jobLevel: text("job_level"),
  salaryBase: decimal("salary_base", { precision: 12, scale: 2 }).notNull(),
  equityShares: integer("equity_shares"),
  signingBonus: decimal("signing_bonus", { precision: 12, scale: 2 }),
  performanceBonus: decimal("performance_bonus", { precision: 12, scale: 2 }),
  offerLetterTemplate: text("offer_letter_template"), // HTML template for the offer letter
  offerLetterUrl: text("offer_letter_url"),
  previewToken: text("preview_token").unique(), // Token for hiring manager to preview offer
  acceptanceToken: text("acceptance_token").unique().notNull(),
  verificationToken: text("verification_token"),
  verificationExpiresAt: timestamp("verification_expires_at"),
  status: text("status").notNull().default("waiting_approval"), // waiting_approval | approved | rejected | sent | accepted | declined
  approvedBy: varchar("approved_by").references(() => employees.id, { onDelete: "set null" }),
  approvedAt: timestamp("approved_at"),
  rejectedBy: varchar("rejected_by").references(() => employees.id, { onDelete: "set null" }),
  rejectedAt: timestamp("rejected_at"),
  rejectionReason: text("rejection_reason"),
  sentAt: timestamp("sent_at"),
  respondedAt: timestamp("responded_at"),
  createdBy: varchar("created_by").references(() => employees.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // PayMix fields
  allowPaymixAdjustment: boolean("allow_paymix_adjustment").notNull().default(false),
  // Per-offer PayMix variation as a percentage (e.g. 10 = ±10%). Falls back to
  // PAYMIX_DEFAULT_VARIATION_PCT when absent. Capped at PAYMIX_MAX_VARIATION_PCT.
  paymixVariationPct: integer("paymix_variation_pct").notNull().default(10),
  fxRate: decimal("fx_rate", { precision: 8, scale: 4 }),
  salaryTableId: varchar("salary_table_id"),
  salaryTableBandId: varchar("salary_table_band_id"),
  acceptedSalaryBase: decimal("accepted_salary_base", { precision: 12, scale: 2 }),
  acceptedEquityShares: integer("accepted_equity_shares"),
  offerRecommendationId: varchar("offer_recommendation_id").references(
    (): AnyPgColumn => offerRecommendations.id,
    { onDelete: "set null" },
  ),
  excitementScoreId: varchar("excitement_score_id").references(
    (): AnyPgColumn => excitementScores.id,
    { onDelete: "set null" },
  ),
  hmFollowedRecommendation: boolean("hm_followed_recommendation"),
  hmDeviationReason: text("hm_deviation_reason"),
  hmDeviationFields: jsonb("hm_deviation_fields").$type<{
    salary?: { recommended: string; adjusted: string };
    equity?: { recommended: string; adjusted: string };
    signingBonus?: { recommended: string; adjusted: string };
    performanceBonus?: { recommended: string; adjusted: string };
  }>(),
  performanceBonusShares: integer("performance_bonus_shares"),
  performanceBonusSharesMax: integer("performance_bonus_shares_max"),
  marketCompEstimate: jsonb("market_comp_estimate").$type<MarketCompEstimateJson | null>(),
});

export const offersRelations = relations(offers, ({ one }) => ({
  application: one(applications, {
    fields: [offers.applicationId],
    references: [applications.id],
  }),
  approver: one(employees, {
    fields: [offers.approvedBy],
    references: [employees.id],
    relationName: "approverOffer",
  }),
  rejector: one(employees, {
    fields: [offers.rejectedBy],
    references: [employees.id],
    relationName: "rejectorOffer",
  }),
  creator: one(employees, {
    fields: [offers.createdBy],
    references: [employees.id],
    relationName: "creatorOffer",
  }),
  recommendation: one(offerRecommendations, {
    fields: [offers.offerRecommendationId],
    references: [offerRecommendations.id],
  }),
  excitementScore: one(excitementScores, {
    fields: [offers.excitementScoreId],
    references: [excitementScores.id],
  }),
}));

export const insertOfferSchema = createInsertSchema(offers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  approvedBy: true,
  approvedAt: true,
  sentAt: true,
  respondedAt: true,
});

export type InsertOffer = z.infer<typeof insertOfferSchema>;
export type Offer = typeof offers.$inferSelect;

// ==================== ATS - OFFER RECOMMENDATIONS ====================
export type OfferRecommendationReasoning = {
  differentiators: Array<{
    signal: string;
    source: string[];
    convergent: boolean;
    points: number;
  }>;
  totalPoints: number;
  contextNotes: string[];
  equityCheck: { peerComparison: string };
  justification: string;
};

export const offerRecommendations = pgTable(
  "offer_recommendations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .references(() => applications.id, { onDelete: "cascade" })
      .notNull(),
    skillId: integer("skill_id").notNull(),
    skillVersion: integer("skill_version").notNull(),
    salary: decimal("salary", { precision: 12, scale: 2 }).notNull(),
    equity: decimal("equity", { precision: 10, scale: 4 }).notNull(),
    zone: text("zone").notNull(),
    bandPosition: decimal("band_position", { precision: 5, scale: 2 }).notNull(),
    negotiationFloor: decimal("negotiation_floor", { precision: 12, scale: 2 }).notNull(),
    negotiationCeiling: decimal("negotiation_ceiling", { precision: 12, scale: 2 }).notNull(),
    reasoning: jsonb("reasoning").$type<OfferRecommendationReasoning>().notNull(),
    excitementScoreId: varchar("excitement_score_id").references(() => excitementScores.id, {
      onDelete: "set null",
    }),
    createdBy: varchar("created_by").references(() => employees.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    applicationIdx: index("offer_recommendations_application_idx").on(t.applicationId, t.createdAt),
  }),
);

export const offerRecommendationsRelations = relations(offerRecommendations, ({ one }) => ({
  application: one(applications, {
    fields: [offerRecommendations.applicationId],
    references: [applications.id],
  }),
  creator: one(employees, {
    fields: [offerRecommendations.createdBy],
    references: [employees.id],
  }),
  excitementScore: one(excitementScores, {
    fields: [offerRecommendations.excitementScoreId],
    references: [excitementScores.id],
  }),
}));

export const insertOfferRecommendationSchema = createInsertSchema(offerRecommendations).omit({
  id: true,
  createdAt: true,
});
export type InsertOfferRecommendation = z.infer<typeof insertOfferRecommendationSchema>;
export type OfferRecommendation = typeof offerRecommendations.$inferSelect;

// ==================== EXCITEMENT SCORES ====================
export const excitementScores = pgTable(
  "excitement_scores",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .references(() => applications.id, { onDelete: "cascade" })
      .notNull(),
    excitementScore: integer("excitement_score").notNull(),
    rationale: text("rationale").notNull(),
    promptVersion: text("prompt_version").notNull(),
    skillVersionId: varchar("skill_version_id").references((): AnyPgColumn => skillVersions.id, {
      onDelete: "set null",
    }),
    dossierSnapshot: text("dossier_snapshot").notNull(),
    signals:
      jsonb("signals").$type<
        Array<{
          signal: string;
          source: string[];
          convergent: boolean;
          points: number;
        }>
      >(),
    computedBy: varchar("computed_by").references(() => employees.id, { onDelete: "set null" }),
    overriddenScore: integer("overridden_score"),
    overrideReason: text("override_reason"),
    overriddenBy: varchar("overridden_by").references(() => employees.id, { onDelete: "set null" }),
    marketCompEstimate: jsonb("market_comp_estimate").$type<MarketCompEstimateJson | null>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    applicationIdx: index("excitement_scores_application_idx").on(t.applicationId, t.createdAt),
  }),
);

export type MarketCompEstimateJson = {
  salaryMonthly: number;
  salaryAnnual: number;
  totalCashMonthly: number | null;
  totalCashAnnual: number | null;
  source: "benchmark" | "manual";
  discipline: string;
  level: string;
  currentCompany: string | null;
  currentRole: string | null;
  editedByHM: boolean;
  originalEstimate: {
    salaryMonthly: number;
    totalCashMonthly: number | null;
  } | null;
};

export const excitementScoresRelations = relations(excitementScores, ({ one }) => ({
  application: one(applications, {
    fields: [excitementScores.applicationId],
    references: [applications.id],
  }),
  computedByEmployee: one(employees, {
    fields: [excitementScores.computedBy],
    references: [employees.id],
  }),
}));

export const insertExcitementScoreSchema = createInsertSchema(excitementScores).omit({
  id: true,
  createdAt: true,
});
export type InsertExcitementScore = z.infer<typeof insertExcitementScoreSchema>;
export type ExcitementScore = typeof excitementScores.$inferSelect;

// ==================== CANDIDATE EXCITEMENT SCORES ====================
// Mirror of excitement_scores but for the candidate's side: how excited is the
// candidate about Comp. Generated passively from interview transcripts, one
// row per interview completion → forms a per-application trajectory.
export const candidateExcitementScores = pgTable(
  "candidate_excitement_scores",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .references(() => applications.id, { onDelete: "cascade" })
      .notNull(),
    interviewId: varchar("interview_id")
      .references(() => interviews.id, { onDelete: "cascade" })
      .notNull(),
    // Sequential index of this interview within the application's stages
    // (1, 2, 3...). Stored once at compute time so we don't corrupt history
    // if the job's stages change later.
    stageSequence: integer("stage_sequence").notNull(),
    excitementScore: integer("excitement_score").notNull(),
    rationale: text("rationale").notNull(),
    signals:
      jsonb("signals").$type<
        Array<{
          signal: string;
          source: string[];
          convergent: boolean;
          points: number;
        }>
      >(),
    // Optional confidence sentinel — skill can flag low-quality transcripts.
    confidence: text("confidence"), // "high" | "medium" | "low"
    promptVersion: text("prompt_version").notNull(),
    skillVersionId: varchar("skill_version_id").references((): AnyPgColumn => skillVersions.id, {
      onDelete: "set null",
    }),
    // Override fields — populated when HM disagrees with the AI score.
    // Empty in PR1 (no override UI yet); wired in PR3.
    overriddenScore: integer("overridden_score"),
    overrideReason: text("override_reason"),
    overrideCategory: text("override_category"), // ai_missed_signal | ai_misread_tone | context_outside_transcript | ai_correct_but_stale | other
    overriddenBy: varchar("overridden_by").references(() => employees.id, { onDelete: "set null" }),
    overriddenAt: timestamp("overridden_at"),
    // Tracks whether the HM has seen this score in the offer dialog — used to
    // distinguish "implicit agreement" from "never looked at it".
    hmViewedAt: timestamp("hm_viewed_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    applicationIdx: index("candidate_excitement_scores_application_idx").on(
      t.applicationId,
      t.createdAt,
    ),
    interviewIdx: index("candidate_excitement_scores_interview_idx").on(t.interviewId),
  }),
);

export const candidateExcitementScoresRelations = relations(
  candidateExcitementScores,
  ({ one }) => ({
    application: one(applications, {
      fields: [candidateExcitementScores.applicationId],
      references: [applications.id],
    }),
    interview: one(interviews, {
      fields: [candidateExcitementScores.interviewId],
      references: [interviews.id],
    }),
    overriddenByEmployee: one(employees, {
      fields: [candidateExcitementScores.overriddenBy],
      references: [employees.id],
    }),
  }),
);

export const insertCandidateExcitementScoreSchema = createInsertSchema(
  candidateExcitementScores,
).omit({
  id: true,
  createdAt: true,
});
export type InsertCandidateExcitementScore = z.infer<typeof insertCandidateExcitementScoreSchema>;
export type CandidateExcitementScore = typeof candidateExcitementScores.$inferSelect;

// ==================== OFFER RECOMMENDATION GENERATIONS (audit log) ====================
export const offerRecommendationGenerations = pgTable(
  "offer_recommendation_generations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    applicationId: varchar("application_id")
      .references(() => applications.id, { onDelete: "cascade" })
      .notNull(),
    salaryTableId: varchar("salary_table_id"),
    salaryTableBandId: varchar("salary_table_band_id"),
    fxRate: decimal("fx_rate", { precision: 8, scale: 4 }),
    status: text("status").notNull(),
    failureStage: text("failure_stage"),
    errorMessage: text("error_message"),
    promptVersion: text("prompt_version"),
    skillVersionId: varchar("skill_version_id").references((): AnyPgColumn => skillVersions.id, {
      onDelete: "set null",
    }),
    latencyMs: integer("latency_ms"),
    excitementScoreId: varchar("excitement_score_id").references(() => excitementScores.id, {
      onDelete: "set null",
    }),
    offerRecommendationId: varchar("offer_recommendation_id").references(
      () => offerRecommendations.id,
      { onDelete: "set null" },
    ),
    triggeredBy: varchar("triggered_by").references(() => employees.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    applicationIdx: index("offer_rec_generations_application_idx").on(t.applicationId, t.createdAt),
  }),
);

export const offerRecommendationGenerationsRelations = relations(
  offerRecommendationGenerations,
  ({ one }) => ({
    application: one(applications, {
      fields: [offerRecommendationGenerations.applicationId],
      references: [applications.id],
    }),
    excitementScore: one(excitementScores, {
      fields: [offerRecommendationGenerations.excitementScoreId],
      references: [excitementScores.id],
    }),
    offerRecommendation: one(offerRecommendations, {
      fields: [offerRecommendationGenerations.offerRecommendationId],
      references: [offerRecommendations.id],
    }),
    triggerer: one(employees, {
      fields: [offerRecommendationGenerations.triggeredBy],
      references: [employees.id],
    }),
  }),
);

export type OfferRecommendationGeneration = typeof offerRecommendationGenerations.$inferSelect;
export const insertOfferRecommendationGenerationSchema = createInsertSchema(
  offerRecommendationGenerations,
).omit({ id: true, createdAt: true });
export type InsertOfferRecommendationGeneration = z.infer<
  typeof insertOfferRecommendationGenerationSchema
>;

// ==================== OFFER LETTER TEMPLATES ====================
export const offerLetterTemplates = pgTable("offer_letter_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  htmlContent: text("html_content").notNull(),
  isDefault: boolean("is_default").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").references(() => employees.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const offerLetterTemplatesRelations = relations(offerLetterTemplates, ({ one }) => ({
  creator: one(employees, {
    fields: [offerLetterTemplates.createdBy],
    references: [employees.id],
  }),
}));

export const insertOfferLetterTemplateSchema = createInsertSchema(offerLetterTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertOfferLetterTemplate = z.infer<typeof insertOfferLetterTemplateSchema>;
export type OfferLetterTemplate = typeof offerLetterTemplates.$inferSelect;

// ==================== PERFORMANCE MANAGEMENT ====================
export const performanceCycles = pgTable("performance_cycles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  employeeId: varchar("employee_id")
    .references(() => employees.id, { onDelete: "cascade" })
    .notNull(),
  cycleType: text("cycle_type").notNull(), // first_3m | annual
  cycleDate: date("cycle_date").notNull(),
  dueDate: date("due_date").notNull(),
  status: text("status").notNull().default("pending"), // pending | in_progress | completed
  createdAt: timestamp("created_at").defaultNow(),
});

export const performanceCyclesRelations = relations(performanceCycles, ({ one, many }) => ({
  employee: one(employees, {
    fields: [performanceCycles.employeeId],
    references: [employees.id],
  }),
  managerAssessments: many(managerAssessments),
  performanceOutcomes: many(performanceOutcomes),
}));

export const insertPerformanceCycleSchema = createInsertSchema(performanceCycles).omit({
  id: true,
  createdAt: true,
});

export type InsertPerformanceCycle = z.infer<typeof insertPerformanceCycleSchema>;
export type PerformanceCycle = typeof performanceCycles.$inferSelect;

// ==================== MANAGER ASSESSMENT ====================
export const managerAssessments = pgTable("manager_assessments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  performanceCycleId: varchar("performance_cycle_id")
    .references(() => performanceCycles.id)
    .notNull(),
  managerId: varchar("manager_id")
    .references(() => employees.id, { onDelete: "cascade" })
    .notNull(),
  ratingFinal: text("rating_final").notNull(), // meets | doesnt_meet
  observations: text("observations").notNull(),
  submittedAt: timestamp("submitted_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const managerAssessmentsRelations = relations(managerAssessments, ({ one }) => ({
  performanceCycle: one(performanceCycles, {
    fields: [managerAssessments.performanceCycleId],
    references: [performanceCycles.id],
  }),
  manager: one(employees, {
    fields: [managerAssessments.managerId],
    references: [employees.id],
  }),
}));

export const insertManagerAssessmentSchema = createInsertSchema(managerAssessments).omit({
  id: true,
  createdAt: true,
});

export type InsertManagerAssessment = z.infer<typeof insertManagerAssessmentSchema>;
export type ManagerAssessment = typeof managerAssessments.$inferSelect;

// ==================== PERFORMANCE OUTCOME ====================
export const performanceOutcomes = pgTable("performance_outcomes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  performanceCycleId: varchar("performance_cycle_id")
    .references(() => performanceCycles.id)
    .notNull(),
  ratingFinal: text("rating_final").notNull(),
  salaryIncreasePct: integer("salary_increase_pct").notNull(),
  newSalaryBase: decimal("new_salary_base", { precision: 12, scale: 2 }),
  adminNotes: text("admin_notes"),
  finalizedAt: timestamp("finalized_at").notNull(),
});

export const performanceOutcomesRelations = relations(performanceOutcomes, ({ one }) => ({
  performanceCycle: one(performanceCycles, {
    fields: [performanceOutcomes.performanceCycleId],
    references: [performanceCycles.id],
  }),
}));

export const insertPerformanceOutcomeSchema = createInsertSchema(performanceOutcomes).omit({
  id: true,
});

export type InsertPerformanceOutcome = z.infer<typeof insertPerformanceOutcomeSchema>;
export type PerformanceOutcome = typeof performanceOutcomes.$inferSelect;

// ==================== PULSE SURVEYS ====================
export const pulseSurveys = pgTable(
  "pulse_surveys",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    title: text("title").notNull(),
    description: text("description"),

    // NOVOS CAMPOS
    surveyType: text("survey_type").notNull().default("adhoc"), // 'adhoc' | 'recurring'
    recurrenceInDays: integer("recurrence_in_days"), // NULL = não recorrente, número = recorre a cada X dias
    /** @deprecated Onboarding pulse-survey flow was removed; column is unread. Drop in a future migration. */
    onboardingTriggerDays: integer("onboarding_trigger_days"),
    deadlineInDays: integer("deadline_in_days").notNull().default(7),
    firstSendDate: timestamp("first_send_date"), // Data do primeiro envio para recurring surveys
    nextInstanceDate: timestamp("next_instance_date"), // Data da próxima instância (null = pausada ou não recorrente)
    isActive: boolean("is_active").notNull().default(true),

    // MANTER CAMPOS EXISTENTES (mas remover scheduledSendAt, deadline e status)
    createdBy: varchar("created_by").references(() => employees.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_pulse_surveys_active_type").on(
      table.isActive,
      table.surveyType,
      table.nextInstanceDate,
    ),
  ],
);

export const pulseSurveysRelations = relations(pulseSurveys, ({ one, many }) => ({
  creator: one(employees, {
    fields: [pulseSurveys.createdBy],
    references: [employees.id],
  }),
  questions: many(pulseQuestions),
  instances: many(pulseSurveyInstances), // NOVO
  exclusions: many(pulseSurveyEmployeeExclusions),
  // REMOVIDO: responses, remindersSent (agora vinculados à instância)
}));

export const insertPulseSurveySchema = createInsertSchema(pulseSurveys).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPulseSurvey = z.infer<typeof insertPulseSurveySchema>;
export type PulseSurvey = typeof pulseSurveys.$inferSelect;

// ==================== PULSE SURVEY INSTANCES ====================
export const pulseSurveyInstances = pgTable(
  "pulse_survey_instances",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    pulseSurveyId: varchar("pulse_survey_id")
      .references(() => pulseSurveys.id)
      .notNull(),
    sentAt: timestamp("sent_at"),
    deadline: timestamp("deadline"),
    status: text("status").notNull().default("active"), // 'draft' | 'active' | 'completed' | 'cancelled'
    // Set when the draft-notifier cron pings the configured notifiers. Used for
    // same-BRT-day dedup so the cron is safe to run more than once a day.
    lastNotifierPingAt: timestamp("last_notifier_ping_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    idxInstanceStatus: index("idx_pulse_survey_instances_status").on(table.status),
  }),
);

export const pulseSurveyInstancesRelations = relations(pulseSurveyInstances, ({ one, many }) => ({
  survey: one(pulseSurveys, {
    fields: [pulseSurveyInstances.pulseSurveyId],
    references: [pulseSurveys.id],
  }),
  questionInstances: many(pulseQuestionInstances),
  responses: many(pulseResponses),
  remindersSent: many(pulseRemindersSent),
}));

export const insertPulseSurveyInstanceSchema = createInsertSchema(pulseSurveyInstances).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPulseSurveyInstance = z.infer<typeof insertPulseSurveyInstanceSchema>;
export type PulseSurveyInstance = typeof pulseSurveyInstances.$inferSelect;

// ==================== PULSE QUESTIONS ====================
export const pulseQuestions = pgTable("pulse_questions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pulseSurveyId: varchar("pulse_survey_id")
    .references(() => pulseSurveys.id)
    .notNull(),
  questionText: text("question_text").notNull(),
  description: text("description"),

  questionType: text("question_type").notNull().default("short_text"),
  required: boolean("required").notNull().default(false),

  scaleMin: integer("scale_min"),
  scaleMax: integer("scale_max"),
  scaleMinLabel: text("scale_min_label"),
  scaleMaxLabel: text("scale_max_label"),

  options: jsonb("options"),

  questionOrder: integer("question_order").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const pulseQuestionsRelations = relations(pulseQuestions, ({ one, many }) => ({
  pulseSurvey: one(pulseSurveys, {
    fields: [pulseQuestions.pulseSurveyId],
    references: [pulseSurveys.id],
  }),
  answers: many(pulseAnswers),
}));

export const insertPulseQuestionSchema = createInsertSchema(pulseQuestions).omit({
  id: true,
  createdAt: true,
});

export type InsertPulseQuestion = z.infer<typeof insertPulseQuestionSchema>;
export type PulseQuestion = typeof pulseQuestions.$inferSelect;

// ==================== PULSE QUESTION INSTANCES ====================
export const pulseQuestionInstances = pgTable("pulse_question_instances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pulseSurveyInstanceId: varchar("pulse_survey_instance_id")
    .references(() => pulseSurveyInstances.id)
    .notNull(),
  pulseQuestionId: varchar("pulse_question_id")
    .references(() => pulseQuestions.id)
    .notNull(),
  questionText: text("question_text").notNull(),
  description: text("description"),
  questionType: text("question_type").notNull().default("short_text"),
  required: boolean("required").notNull().default(false),
  scaleMin: integer("scale_min"),
  scaleMax: integer("scale_max"),
  scaleMinLabel: text("scale_min_label"),
  scaleMaxLabel: text("scale_max_label"),
  options: jsonb("options"),
  questionOrder: integer("question_order").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const pulseQuestionInstancesRelations = relations(
  pulseQuestionInstances,
  ({ one, many }) => ({
    surveyInstance: one(pulseSurveyInstances, {
      fields: [pulseQuestionInstances.pulseSurveyInstanceId],
      references: [pulseSurveyInstances.id],
    }),
    originalQuestion: one(pulseQuestions, {
      fields: [pulseQuestionInstances.pulseQuestionId],
      references: [pulseQuestions.id],
    }),
    answers: many(pulseAnswers),
  }),
);

export const insertPulseQuestionInstanceSchema = createInsertSchema(pulseQuestionInstances).omit({
  id: true,
  createdAt: true,
});

export type InsertPulseQuestionInstance = z.infer<typeof insertPulseQuestionInstanceSchema>;
export type PulseQuestionInstance = typeof pulseQuestionInstances.$inferSelect;

// ==================== PULSE RESPONSES ====================
export const pulseResponses = pgTable(
  "pulse_responses",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

    // ATUALIZAR: vincular à instância ao invés do survey diretamente
    pulseSurveyInstanceId: varchar("pulse_survey_instance_id")
      .references(() => pulseSurveyInstances.id)
      .notNull(),

    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    status: varchar("status", { length: 20 }).notNull().default("pending"), // 'pending' | 'in_progress' | 'completed'
    submittedAt: timestamp("submitted_at"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueResponse: unique().on(table.pulseSurveyInstanceId, table.employeeId),
    idxResponseInstanceStatus: index("idx_pulse_responses_instance_status").on(
      table.pulseSurveyInstanceId,
      table.status,
    ),
  }),
);

export const pulseResponsesRelations = relations(pulseResponses, ({ one, many }) => ({
  instance: one(pulseSurveyInstances, {
    fields: [pulseResponses.pulseSurveyInstanceId],
    references: [pulseSurveyInstances.id],
  }),
  employee: one(employees, {
    fields: [pulseResponses.employeeId],
    references: [employees.id],
  }),
  answers: many(pulseAnswers),
}));

export const insertPulseResponseSchema = createInsertSchema(pulseResponses).omit({
  id: true,
  createdAt: true,
});

export type InsertPulseResponse = z.infer<typeof insertPulseResponseSchema>;
export type PulseResponse = typeof pulseResponses.$inferSelect;

// ==================== PULSE ANSWERS ====================
export const pulseAnswers = pgTable("pulse_answers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pulseResponseId: varchar("pulse_response_id")
    .references(() => pulseResponses.id)
    .notNull(),
  pulseQuestionInstanceId: varchar("pulse_question_instance_id")
    .references(() => pulseQuestionInstances.id)
    .notNull(),

  answerText: text("answer_text"),
  answerValue: integer("answer_value"),

  createdAt: timestamp("created_at").defaultNow(),
});

export const pulseAnswersRelations = relations(pulseAnswers, ({ one }) => ({
  pulseResponse: one(pulseResponses, {
    fields: [pulseAnswers.pulseResponseId],
    references: [pulseResponses.id],
  }),
  pulseQuestionInstance: one(pulseQuestionInstances, {
    fields: [pulseAnswers.pulseQuestionInstanceId],
    references: [pulseQuestionInstances.id],
  }),
}));

export const insertPulseAnswerSchema = createInsertSchema(pulseAnswers).omit({
  id: true,
  createdAt: true,
});

export type InsertPulseAnswer = z.infer<typeof insertPulseAnswerSchema>;
export type PulseAnswer = typeof pulseAnswers.$inferSelect;

// ==================== PULSE REMINDERS SENT ====================
export const PULSE_REMINDER_TYPES = [
  "INDIVIDUAL_REMINDER",
  "DEADLINE_REMINDER",
  "GENERAL_REMINDER",
  "MANAGER_ESCALATION",
  "LAST_DAY_REMINDER",
  "LAST_DAY_MANAGER_ESCALATION",
  "REOPEN_REMINDER",
  "MANUAL_REMINDER",
] as const;
export type PulseReminderType = (typeof PULSE_REMINDER_TYPES)[number];
export const pulseReminderTypeSchema = z.enum(PULSE_REMINDER_TYPES);

export const pulseRemindersSent = pgTable(
  "pulse_reminders_sent",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

    // ATUALIZAR: vincular à instância
    pulseSurveyInstanceId: varchar("pulse_survey_instance_id")
      .references(() => pulseSurveyInstances.id)
      .notNull(),

    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    reminderNumber: integer("reminder_number"),
    type: text("type").notNull().default("INDIVIDUAL_REMINDER"),
    sentAt: timestamp("sent_at").notNull(),
  },
  (table) => ({
    idxReminderLookup: index("idx_pulse_reminders_instance_employee_type").on(
      table.pulseSurveyInstanceId,
      table.employeeId,
      table.type,
    ),
  }),
);

export const pulseRemindersSentRelations = relations(pulseRemindersSent, ({ one }) => ({
  instance: one(pulseSurveyInstances, {
    fields: [pulseRemindersSent.pulseSurveyInstanceId],
    references: [pulseSurveyInstances.id],
  }),
  employee: one(employees, {
    fields: [pulseRemindersSent.employeeId],
    references: [employees.id],
  }),
}));

export const insertPulseReminderSentSchema = createInsertSchema(pulseRemindersSent).omit({
  id: true,
});

export type InsertPulseReminderSent = z.infer<typeof insertPulseReminderSentSchema>;
export type PulseReminderSent = typeof pulseRemindersSent.$inferSelect;

// ==================== ONBOARDING TEMPLATES ====================
export const ONBOARDING_DISCIPLINES = [
  "cx",
  "comp_execs",
  "growth",
  "gtm_engineering",
  "sales",
  "tech",
  "product",
  "backoffice",
] as const;
export type OnboardingDiscipline = (typeof ONBOARDING_DISCIPLINES)[number];

export const ONBOARDING_TEMPLATE_STATES = ["draft", "published"] as const;
export type OnboardingTemplateState = (typeof ONBOARDING_TEMPLATE_STATES)[number];

export const onboardingTemplates = pgTable(
  "onboarding_templates",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    name: text("name").notNull(),
    sidebarBlockLabel: text("sidebar_block_label").notNull(),
    ownerId: varchar("owner_id")
      .references(() => employees.id, { onDelete: "restrict" })
      .notNull(),
    state: text("state").notNull().default("draft"), // OnboardingTemplateState
    // Per-milestone welcome messages, keyed by milestone day as string ("1", "2", "3", "15", "30", "60").
    welcomeMessages: jsonb("welcome_messages")
      .$type<Record<string, string>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    unique("onboarding_templates_name_unique").on(table.name),
    index("idx_onboarding_templates_owner_id").on(table.ownerId),
  ],
);

export const onboardingTemplatesRelations = relations(onboardingTemplates, ({ one }) => ({
  owner: one(employees, {
    fields: [onboardingTemplates.ownerId],
    references: [employees.id],
  }),
}));

export const insertOnboardingTemplateSchema = createInsertSchema(onboardingTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertOnboardingTemplate = z.infer<typeof insertOnboardingTemplateSchema>;
export type OnboardingTemplate = typeof onboardingTemplates.$inferSelect;

// ==================== ONBOARDING AUDIT LOG (BR-G-002) ====================
export const onboardingAuditLog = pgTable(
  "onboarding_audit_log",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    actorClerkId: text("actor_clerk_id").notNull(),
    action: text("action").notNull(), // e.g. "template.created"
    targetType: text("target_type").notNull(), // e.g. "onboarding_template"
    targetId: varchar("target_id").notNull(),
    before: jsonb("before"),
    after: jsonb("after"),
    params: jsonb("params"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_onboarding_audit_target").on(table.targetType, table.targetId),
    index("idx_onboarding_audit_created_at").on(table.createdAt),
  ],
);

export type OnboardingAuditLog = typeof onboardingAuditLog.$inferSelect;

// ==================== ONBOARDING TEMPLATE SUBSECTIONS (UC-TPL-003) ====================
export const onboardingTemplateSubsections = pgTable(
  "onboarding_template_subsections",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "cascade" })
      .notNull(),
    label: text("label").notNull(),
    icon: text("icon").notNull().default("folder"),
    position: integer("position").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    unique("onboarding_template_subsections_template_label_unique").on(
      table.templateId,
      table.label,
    ),
    index("idx_onboarding_template_subsections_template_position").on(
      table.templateId,
      table.position,
    ),
  ],
);

export const onboardingTemplateSubsectionsRelations = relations(
  onboardingTemplateSubsections,
  ({ one }) => ({
    template: one(onboardingTemplates, {
      fields: [onboardingTemplateSubsections.templateId],
      references: [onboardingTemplates.id],
    }),
  }),
);

export type OnboardingTemplateSubsection = typeof onboardingTemplateSubsections.$inferSelect;

// ==================== ONBOARDING TEMPLATE CHANNELS (UC-TPL-004) ====================
export const onboardingTemplateChannels = pgTable(
  "onboarding_template_channels",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "cascade" })
      .notNull(),
    channelName: text("channel_name").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    unique("onboarding_template_channels_template_channel_unique").on(
      table.templateId,
      table.channelName,
    ),
    index("idx_onboarding_template_channels_template_id").on(table.templateId),
  ],
);

export const onboardingTemplateChannelsRelations = relations(
  onboardingTemplateChannels,
  ({ one }) => ({
    template: one(onboardingTemplates, {
      fields: [onboardingTemplateChannels.templateId],
      references: [onboardingTemplates.id],
    }),
  }),
);

export type OnboardingTemplateChannel = typeof onboardingTemplateChannels.$inferSelect;

// ==================== ONBOARDING TEMPLATE MILESTONES (UC-TPL-005) ====================
// BR-TPL-009: day numbers are the fixed set {1, 2, 3, 15, 30, 60}.
export const ONBOARDING_MILESTONE_DAYS = [1, 2, 3, 15, 30, 60] as const;
export type OnboardingMilestoneDay = (typeof ONBOARDING_MILESTONE_DAYS)[number];

export type OnboardingMilestoneExpectedItem = {
  id: string;
  title: string;
  description: string | null;
};

export const onboardingTemplateMilestones = pgTable(
  "onboarding_template_milestones",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "cascade" })
      .notNull(),
    dayNumber: integer("day_number").notNull(),
    label: text("label").notNull(),
    goal: text("goal").notNull().default(""),
    expectedItems: jsonb("expected_items")
      .$type<OnboardingMilestoneExpectedItem[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    notExpectedItems: jsonb("not_expected_items")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("onboarding_template_milestones_template_day_unique").on(
      table.templateId,
      table.dayNumber,
    ),
    index("idx_onboarding_template_milestones_template_id").on(table.templateId),
  ],
);

export const onboardingTemplateMilestonesRelations = relations(
  onboardingTemplateMilestones,
  ({ one }) => ({
    template: one(onboardingTemplates, {
      fields: [onboardingTemplateMilestones.templateId],
      references: [onboardingTemplates.id],
    }),
  }),
);

export type OnboardingTemplateMilestone = typeof onboardingTemplateMilestones.$inferSelect;

// ==================== ONBOARDING TEMPLATE TOOLS (UC-TPL-006) ====================
export const onboardingTemplateTools = pgTable(
  "onboarding_template_tools",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "cascade" })
      .notNull(),
    name: text("name").notNull(),
    note: text("note").notNull().default(""),
    ownerEmail: text("owner_email").notNull(),
    link: text("link").notNull().default(""),
    position: integer("position").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_onboarding_template_tools_template_id").on(table.templateId),
    index("idx_onboarding_template_tools_template_position").on(table.templateId, table.position),
  ],
);

export const onboardingTemplateToolsRelations = relations(onboardingTemplateTools, ({ one }) => ({
  template: one(onboardingTemplates, {
    fields: [onboardingTemplateTools.templateId],
    references: [onboardingTemplates.id],
  }),
}));

export type OnboardingTemplateTool = typeof onboardingTemplateTools.$inferSelect;

// ==================== ONBOARDING TEMPLATE TODOS ====================
// ==================== ONBOARDING INSTANCE (UC-ONB-*) ====================

export const ONBOARDING_LEVELS = ["L0", "L1", "L2", "L3", "L4", "L5", "L6", "L7"] as const;
export type OnboardingLevel = (typeof ONBOARDING_LEVELS)[number];

export const ONBOARDING_STATES = ["draft", "active", "canceled", "graduated"] as const;
export type OnboardingState = (typeof ONBOARDING_STATES)[number];

// BR-ONB-016/017: TTP is either a day (1..90) or the literal "still_not_there".
export const TTP_STILL_NOT_THERE = "still_not_there" as const;

export const onboardings = pgTable(
  "onboardings",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    hireEmail: text("hire_email").notNull(),
    hireFullName: text("hire_full_name").notNull(),
    discipline: text("discipline").notNull(), // OnboardingDiscipline
    level: text("level").notNull(), // OnboardingLevel
    startDate: date("start_date").notNull(),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "restrict" })
      .notNull(),
    buddyEmployeeId: varchar("buddy_employee_id")
      .references(() => employees.id, { onDelete: "restrict" })
      .notNull(),
    firingManagerEmployeeId: varchar("firing_manager_employee_id")
      .references(() => employees.id, { onDelete: "restrict" })
      .notNull(),
    state: text("state").notNull().default("active"), // OnboardingState
    cancellationReason: text("cancellation_reason"),
    canceledAt: timestamp("canceled_at"),
    canceledByClerkId: text("canceled_by_clerk_id"),
    graduatedAt: timestamp("graduated_at"),
    graduatedByClerkId: text("graduated_by_clerk_id"), // null = system (UC-ONB-008)
    // ttpAnswer: null = unanswered or auto-graduated;
    //            "still_not_there" = manager checked the box;
    //            "1".."90" = manager picked the day.
    ttpAnswer: text("ttp_answer"),
    // BR-PORT-012: { "1": iso, "30": iso, ... } — set on first 100% transition, never cleared.
    milestoneCompletionMarks: jsonb("milestone_completion_marks")
      .$type<Record<string, string>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    // Per-milestone welcome messages override. Empty object = inherit all from template.
    welcomeMessages: jsonb("welcome_messages")
      .$type<Record<string, string>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // BR-ONB-001: only one active onboarding per email at a time.
    uniqueIndex("onboardings_hire_email_active_unique")
      .on(table.hireEmail)
      .where(sql`state = 'active'`),
    index("idx_onboardings_state_start_date").on(table.state, table.startDate),
    index("idx_onboardings_template_id").on(table.templateId),
    index("idx_onboardings_firing_manager").on(table.firingManagerEmployeeId),
  ],
);

export const onboardingsRelations = relations(onboardings, ({ one, many }) => ({
  template: one(onboardingTemplates, {
    fields: [onboardings.templateId],
    references: [onboardingTemplates.id],
  }),
  buddy: one(employees, {
    fields: [onboardings.buddyEmployeeId],
    references: [employees.id],
    relationName: "onboardingBuddy",
  }),
  firingManager: one(employees, {
    fields: [onboardings.firingManagerEmployeeId],
    references: [employees.id],
    relationName: "onboardingFiringManager",
  }),
  todos: many(onboardingTodos),
  toolSetups: many(onboardingToolSetups),
  slackAutoJoinJobs: many(slackAutoJoinJobs),
}));

export type Onboarding = typeof onboardings.$inferSelect;

// ---------- to-dos (UC-ONB-004, UC-ONB-005, UC-PORT-004) ----------
export const ONBOARDING_TODO_STATES = ["pending", "done"] as const;
export type OnboardingTodoState = (typeof ONBOARDING_TODO_STATES)[number];

export const ONBOARDING_TODO_DUES = [
  "today",
  "tomorrow_2359",
  "this_week",
  "specific_date",
  "no_deadline",
] as const;
export type OnboardingTodoDue = (typeof ONBOARDING_TODO_DUES)[number];

export const onboardingTodos = pgTable(
  "onboarding_todos",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    title: text("title").notNull(),
    description: text("description"),
    link: text("link"),
    due: text("due").notNull(), // OnboardingTodoDue
    targetDate: timestamp("target_date"), // null when due="no_deadline"
    state: text("state").notNull().default("pending"), // OnboardingTodoState
    authorClerkId: text("author_clerk_id").notNull(),
    authorEmployeeId: varchar("author_employee_id").references(() => employees.id, {
      onDelete: "set null",
    }),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_onboarding_todos_onboarding_state").on(table.onboardingId, table.state),
    index("idx_onboarding_todos_target_date").on(table.targetDate),
  ],
);

export const onboardingTodosRelations = relations(onboardingTodos, ({ one }) => ({
  onboarding: one(onboardings, {
    fields: [onboardingTodos.onboardingId],
    references: [onboardings.id],
  }),
  author: one(employees, {
    fields: [onboardingTodos.authorEmployeeId],
    references: [employees.id],
  }),
}));

export type OnboardingTodo = typeof onboardingTodos.$inferSelect;

// ---------- tool setups (UC-ONB-001 postcondition; flipped by UC-PORT-006) ----------
export const ONBOARDING_TOOL_SETUP_STATES = ["pending", "configured"] as const;
export type OnboardingToolSetupState = (typeof ONBOARDING_TOOL_SETUP_STATES)[number];

export const onboardingToolSetups = pgTable(
  "onboarding_tool_setups",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    toolId: varchar("tool_id")
      .references(() => onboardingTemplateTools.id, { onDelete: "cascade" })
      .notNull(),
    state: text("state").notNull().default("pending"), // OnboardingToolSetupState
    configuredAt: timestamp("configured_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    unique("onboarding_tool_setups_onboarding_tool_unique").on(table.onboardingId, table.toolId),
    index("idx_onboarding_tool_setups_onboarding").on(table.onboardingId),
  ],
);

export const onboardingToolSetupsRelations = relations(onboardingToolSetups, ({ one }) => ({
  onboarding: one(onboardings, {
    fields: [onboardingToolSetups.onboardingId],
    references: [onboardings.id],
  }),
  tool: one(onboardingTemplateTools, {
    fields: [onboardingToolSetups.toolId],
    references: [onboardingTemplateTools.id],
  }),
}));

export type OnboardingToolSetup = typeof onboardingToolSetups.$inferSelect;

// ---------- per-hire milestone item checks (UC-PORT-005) ----------
export const onboardingMilestoneItemChecks = pgTable(
  "onboarding_milestone_item_checks",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    milestoneId: varchar("milestone_id")
      .references(() => onboardingTemplateMilestones.id, { onDelete: "cascade" })
      .notNull(),
    itemIndex: integer("item_index").notNull(), // 0-based index into expected_items JSON
    checkedAt: timestamp("checked_at").defaultNow().notNull(),
  },
  (table) => [
    unique("onb_ms_item_unique").on(table.onboardingId, table.milestoneId, table.itemIndex),
    index("idx_onb_ms_item_onboarding").on(table.onboardingId),
  ],
);
export type OnboardingMilestoneItemCheck = typeof onboardingMilestoneItemChecks.$inferSelect;

// ---------- resource progress per hire (UC-PORT-001 done-detection, UC-PORT-003 process_steps) ----------
export const onboardingResourceProgress = pgTable(
  "onboarding_resource_progress",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    resourceId: varchar("resource_id")
      .references(() => onboardingResources.id, { onDelete: "cascade" })
      .notNull(),
    openedCount: integer("opened_count").notNull().default(0),
    redirectClickedCount: integer("redirect_clicked_count").notNull().default(0),
    checkedSteps: jsonb("checked_steps").$type<number[]>().notNull().default(sql`'[]'::jsonb`),
    // Indices of configured tools for resources whose tools live inline in the
    // resource JSONB `tools` list (setup_checklist / tool_setup without template
    // tool setups). Mirrors checkedSteps.
    checkedTools: jsonb("checked_tools").$type<number[]>().notNull().default(sql`'[]'::jsonb`),
    lastOpenedAt: timestamp("last_opened_at"),
    completedAt: timestamp("completed_at"),
  },
  (table) => [unique("onb_resource_progress_unique").on(table.onboardingId, table.resourceId)],
);
export type OnboardingResourceProgress = typeof onboardingResourceProgress.$inferSelect;

// ---------- tool ask logs (UC-PORT-007) ----------
export const onboardingToolAskLogs = pgTable(
  "onboarding_tool_ask_logs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    toolId: varchar("tool_id")
      .references(() => onboardingTemplateTools.id, { onDelete: "cascade" })
      .notNull(),
    askedAt: timestamp("asked_at").defaultNow().notNull(),
    slackResolved: boolean("slack_resolved").notNull(),
    slackErrorCode: text("slack_error_code"),
  },
  (table) => [index("idx_tool_ask_onb_tool").on(table.onboardingId, table.toolId)],
);
export type OnboardingToolAskLog = typeof onboardingToolAskLogs.$inferSelect;

// ---------- notion mirror cache (UC-PORT-003, BR-PORT-008) ----------
export const onboardingNotionCache = pgTable("onboarding_notion_cache", {
  resourceId: varchar("resource_id")
    .primaryKey()
    .references(() => onboardingResources.id, { onDelete: "cascade" }),
  payload: jsonb("payload").notNull(), // serialized blocks
  fetchedAt: timestamp("fetched_at").defaultNow().notNull(),
});
export type OnboardingNotionCache = typeof onboardingNotionCache.$inferSelect;

// ---------- CSAT D30 (UC-PORT-008, BR-G-009) ----------
export const ONBOARDING_SURVEY_KINDS = ["csat_d30"] as const;
export type OnboardingSurveyKind = (typeof ONBOARDING_SURVEY_KINDS)[number];

export const onboardingSurveyResponses = pgTable(
  "onboarding_survey_responses",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    kind: text("kind").notNull(), // OnboardingSurveyKind
    score: integer("score").notNull(),
    comment: text("comment"),
    submittedAt: timestamp("submitted_at").defaultNow().notNull(),
  },
  (table) => [unique("onb_survey_unique").on(table.onboardingId, table.kind)],
);
export type OnboardingSurveyResponse = typeof onboardingSurveyResponses.$inferSelect;

// ---------- slack auto-join jobs (UC-ONB-001 postcondition; UC-SLACK-* execution) ----------
export const SLACK_AUTO_JOIN_STATES = [
  "queued",
  "joined",
  "failed_bot_not_member",
  "failed_user_not_in_workspace",
  "failed_other",
  "canceled",
] as const;
export type SlackAutoJoinState = (typeof SLACK_AUTO_JOIN_STATES)[number];

export const slackAutoJoinJobs = pgTable(
  "slack_auto_join_jobs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    channelName: text("channel_name").notNull(),
    triggerAt: timestamp("trigger_at").notNull(),
    state: text("state").notNull().default("queued"), // SlackAutoJoinState
    attemptCount: integer("attempt_count").notNull().default(0),
    lastError: text("last_error"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_slack_auto_join_state_trigger_at").on(table.state, table.triggerAt),
    index("idx_slack_auto_join_onboarding").on(table.onboardingId),
  ],
);

export const slackAutoJoinJobsRelations = relations(slackAutoJoinJobs, ({ one }) => ({
  onboarding: one(onboardings, {
    fields: [slackAutoJoinJobs.onboardingId],
    references: [onboardings.id],
  }),
}));

export type SlackAutoJoinJob = typeof slackAutoJoinJobs.$inferSelect;

// ==================== ONBOARDING RESOURCES (UC-TPL-007/008, UC-AUTH-001..009) ====================
export const ONBOARDING_RESOURCE_SCOPES = ["template", "company"] as const;
export type OnboardingResourceScope = (typeof ONBOARDING_RESOURCE_SCOPES)[number];

export const ONBOARDING_RESOURCE_BEHAVIORS = ["embed", "composed", "redirect"] as const;
export type OnboardingResourceBehavior = (typeof ONBOARDING_RESOURCE_BEHAVIORS)[number];

export const ONBOARDING_RESOURCE_RENDER_TYPES = [
  "notion_mirror",
  "process_steps",
  "rich_doc",
  "setup_checklist",
  "tool_setup",
  "slack_channel_info",
  "todo",
] as const;
export type OnboardingResourceRenderType = (typeof ONBOARDING_RESOURCE_RENDER_TYPES)[number];

export const ONBOARDING_RESOURCE_SOURCE_TYPES = [
  "notion",
  "gdoc",
  "pdf",
  "loom",
  "url",
  "inline",
] as const;
export type OnboardingResourceSourceType = (typeof ONBOARDING_RESOURCE_SOURCE_TYPES)[number];

export const ONBOARDING_RESOURCE_DEADLINE_DAYS = [1, 2, 3, 5, 7, 10, 14, 21, 30, 60] as const;
export type OnboardingResourceDeadlineDay = (typeof ONBOARDING_RESOURCE_DEADLINE_DAYS)[number];

export const RENDER_TYPES_PROGRESS_TRACKABLE: ReadonlyArray<OnboardingResourceRenderType> = [
  "process_steps",
  "setup_checklist",
];

export const ONBOARDING_RESOURCE_DEADLINES = [
  "Day 1",
  "Day 2",
  "Day 3",
  "Day 5",
  "Day 7",
  "Day 10",
  "Day 14",
  "Day 21",
  "Day 30",
  "Day 60",
] as const;
export type OnboardingResourceDeadline = (typeof ONBOARDING_RESOURCE_DEADLINES)[number];

export const ONBOARDING_RESOURCE_STATES = ["active", "archived"] as const;
export type OnboardingResourceState = (typeof ONBOARDING_RESOURCE_STATES)[number];

export const ONBOARDING_RESOURCE_CATEGORIES = [
  "sobre_a_comp",
  "dia_a_dia",
  "ferramentas",
  "politicas",
  "cliente",
  "produto",
  "engenharia",
] as const;
export type OnboardingResourceCategory = (typeof ONBOARDING_RESOURCE_CATEGORIES)[number];

// Company-section keys that render flat under the "THE COMPANY" sidebar group
// (no nested collapsible). `company-root` is the canonical key from the
// resources-tab UI; `the_company` is accepted as an alias because authors
// (including MCP flows) often pick it intuitively for "lives at the root".
export const COMPANY_SECTION_FLAT_KEYS: readonly string[] = ["company-root", "the_company"];

export function isCompanyFlatSectionKey(key: string | null | undefined): boolean {
  return key != null && COMPANY_SECTION_FLAT_KEYS.includes(key);
}

export const onboardingResources = pgTable(
  "onboarding_resources",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    scope: text("scope").notNull(),
    templateId: varchar("template_id").references(() => onboardingTemplates.id, {
      onDelete: "cascade",
    }),
    subsectionId: varchar("subsection_id").references(() => onboardingTemplateSubsections.id, {
      onDelete: "set null",
    }),
    companySectionKey: text("company_section_key"),
    companyPosition: integer("company_position").notNull().default(0),
    title: text("title").notNull(),
    deadline: text("deadline"),
    state: text("state").notNull().default("active"),
    behavior: text("behavior").notNull().default("redirect"),
    renderType: text("render_type"),
    sourceType: text("source_type"),
    sourceUrl: text("source_url"),
    contentBlocks: jsonb("content_blocks").$type<unknown[]>().default(sql`'[]'::jsonb`).notNull(),
    sourceRefs: jsonb("source_refs")
      .$type<{ url: string; type: OnboardingResourceSourceType }[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    lastSyncedAt: timestamp("last_synced_at"),
    lastSyncedByClerkId: text("last_synced_by_clerk_id"),
    progressTrackable: boolean("progress_trackable").notNull().default(false),
    version: integer("version").notNull().default(1),
    slackChannels: jsonb("slack_channels")
      .$type<{ name: string; description?: string | null }[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    tools: jsonb("tools")
      .$type<
        { name: string; helpLink?: string | null; required: boolean; description?: string | null }[]
      >()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    disciplines: text("disciplines").array().notNull().default(sql`'{company}'::text[]`),
    category: text("category"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_onboarding_resources_scope_template").on(table.scope, table.templateId),
    index("idx_onboarding_resources_subsection").on(table.subsectionId),
    index("idx_onboarding_resources_state").on(table.state),
  ],
);

export const onboardingResourcesRelations = relations(onboardingResources, ({ one }) => ({
  template: one(onboardingTemplates, {
    fields: [onboardingResources.templateId],
    references: [onboardingTemplates.id],
  }),
  subsection: one(onboardingTemplateSubsections, {
    fields: [onboardingResources.subsectionId],
    references: [onboardingTemplateSubsections.id],
  }),
}));

export type OnboardingResource = typeof onboardingResources.$inferSelect;

// ==================== OTP (One-Time Password) ====================
export const otps = pgTable("otps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  code: text("code").notNull(),
  validUntil: timestamp("valid_until").notNull().default(sql`now() + interval '10 minutes'`),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertOtpSchema = createInsertSchema(otps).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  usedAt: true,
});

export type InsertOtp = z.infer<typeof insertOtpSchema>;
export type Otp = typeof otps.$inferSelect;

// ==================== SLACK PROCESSED EVENTS (Deduplication) ====================
export const slackProcessedEvents = pgTable("slack_processed_events", {
  eventId: text("event_id").primaryKey(),
  processedAt: timestamp("processed_at").notNull().defaultNow(),
});

export const insertSlackProcessedEventSchema = createInsertSchema(slackProcessedEvents);

export type InsertSlackProcessedEvent = z.infer<typeof insertSlackProcessedEventSchema>;
export type SlackProcessedEvent = typeof slackProcessedEvents.$inferSelect;

// ==================== KNOWLEDGE BASE DOCUMENTS ====================
export const kbDocuments = pgTable("kb_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  storagePath: text("storage_path").notNull(),
  contentPath: text("content_path"),
  source: text("source"),
  fileType: text("file_type"),
  fileSize: integer("file_size"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertKbDocumentSchema = createInsertSchema(kbDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertKbDocument = z.infer<typeof insertKbDocumentSchema>;
export type KbDocument = typeof kbDocuments.$inferSelect;

// ==================== JOB APPROVAL APPROVERS ====================
export const jobApprovalApprovers = pgTable(
  "job_approval_approvers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueEmployee: unique().on(table.employeeId),
  }),
);

export const jobApprovalApproversRelations = relations(jobApprovalApprovers, ({ one }) => ({
  employee: one(employees, {
    fields: [jobApprovalApprovers.employeeId],
    references: [employees.id],
  }),
}));

export const insertJobApprovalApproverSchema = createInsertSchema(jobApprovalApprovers).omit({
  id: true,
  createdAt: true,
});

export type InsertJobApprovalApprover = z.infer<typeof insertJobApprovalApproverSchema>;
export type JobApprovalApprover = typeof jobApprovalApprovers.$inferSelect;

// ==================== OFFER APPROVAL APPROVERS ====================
export const offerApprovalApprovers = pgTable(
  "offer_approval_approvers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueEmployee: unique().on(table.employeeId),
  }),
);

export const offerApprovalApproversRelations = relations(offerApprovalApprovers, ({ one }) => ({
  employee: one(employees, {
    fields: [offerApprovalApprovers.employeeId],
    references: [employees.id],
  }),
}));

export const insertOfferApprovalApproverSchema = createInsertSchema(offerApprovalApprovers).omit({
  id: true,
  createdAt: true,
});

export type InsertOfferApprovalApprover = z.infer<typeof insertOfferApprovalApproverSchema>;
export type OfferApprovalApprover = typeof offerApprovalApprovers.$inferSelect;

// ==================== PULSE SURVEY DRAFT NOTIFIERS ====================
export const pulseSurveyDraftNotifiers = pgTable(
  "pulse_survey_draft_notifiers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueEmployee: unique().on(table.employeeId),
  }),
);

export const pulseSurveyDraftNotifiersRelations = relations(
  pulseSurveyDraftNotifiers,
  ({ one }) => ({
    employee: one(employees, {
      fields: [pulseSurveyDraftNotifiers.employeeId],
      references: [employees.id],
    }),
  }),
);

export const insertPulseSurveyDraftNotifierSchema = createInsertSchema(
  pulseSurveyDraftNotifiers,
).omit({
  id: true,
  createdAt: true,
});

export type InsertPulseSurveyDraftNotifier = z.infer<typeof insertPulseSurveyDraftNotifierSchema>;
export type PulseSurveyDraftNotifier = typeof pulseSurveyDraftNotifiers.$inferSelect;

// ==================== PULSE SURVEY EMPLOYEE EXCLUSIONS ====================
export const pulseSurveyEmployeeExclusions = pgTable(
  "pulse_survey_employee_exclusions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    pulseSurveyId: varchar("pulse_survey_id")
      .references(() => pulseSurveys.id, { onDelete: "cascade" })
      .notNull(),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueExclusion: unique().on(table.pulseSurveyId, table.employeeId),
    idxExclusionSurvey: index("idx_pulse_exclusions_survey_id").on(table.pulseSurveyId),
  }),
);

export const pulseSurveyEmployeeExclusionsRelations = relations(
  pulseSurveyEmployeeExclusions,
  ({ one }) => ({
    pulseSurvey: one(pulseSurveys, {
      fields: [pulseSurveyEmployeeExclusions.pulseSurveyId],
      references: [pulseSurveys.id],
    }),
    employee: one(employees, {
      fields: [pulseSurveyEmployeeExclusions.employeeId],
      references: [employees.id],
    }),
  }),
);

export type PulseSurveyEmployeeExclusion = typeof pulseSurveyEmployeeExclusions.$inferSelect;

// ==================== SYSTEM SETTINGS ====================
export const systemSettings = pgTable("system_settings", {
  key: varchar("key").primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type SystemSetting = typeof systemSettings.$inferSelect;

// ==================== ATS - APPLICATION NOTES ====================
export const applicationNotes = pgTable("application_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  applicationId: varchar("application_id")
    .references(() => applications.id)
    .notNull(),
  authorId: varchar("author_id").references(() => employees.id, { onDelete: "set null" }),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  type: varchar("type", { length: 32 }).notNull().default("free"),
  interviewId: varchar("interview_id").references(() => interviews.id, { onDelete: "cascade" }),
  parentId: varchar("parent_id"),
  attachments: text("attachments").array(),
});

export const applicationNotesRelations = relations(applicationNotes, ({ one }) => ({
  application: one(applications, {
    fields: [applicationNotes.applicationId],
    references: [applications.id],
  }),
  author: one(employees, {
    fields: [applicationNotes.authorId],
    references: [employees.id],
  }),
  interview: one(interviews, {
    fields: [applicationNotes.interviewId],
    references: [interviews.id],
  }),
}));

export const insertApplicationNoteSchema = createInsertSchema(applicationNotes).omit({
  id: true,
  createdAt: true,
});

export type InsertApplicationNote = z.infer<typeof insertApplicationNoteSchema>;
export type ApplicationNote = typeof applicationNotes.$inferSelect;

// ==================== NOTION SYNC (Removable) ====================
export const notionSyncState = pgTable(
  "notion_sync_state",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    entityType: varchar("entity_type", { length: 50 }).notNull(),
    notionId: varchar("notion_id", { length: 255 }).notNull(),
    localId: varchar("local_id"),
    syncStatus: varchar("sync_status", { length: 20 }).notNull().default("pending"),
    syncDecision: jsonb("sync_decision"),
    notionData: jsonb("notion_data").notNull(),
    syncedAt: timestamp("synced_at", { withTimezone: true }),
    syncedBy: varchar("synced_by", { length: 255 }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    uniqueEntityNotion: unique().on(table.entityType, table.notionId),
  }),
);

export const insertNotionSyncStateSchema = createInsertSchema(notionSyncState).omit({
  id: true,
  createdAt: true,
});

export type InsertNotionSyncState = z.infer<typeof insertNotionSyncStateSchema>;
export type NotionSyncState = typeof notionSyncState.$inferSelect;

// ==================== GOOGLE CALENDAR SYNC ====================
export const calendarSyncState = pgTable("calendar_sync_state", {
  calendarEmail: varchar("calendar_email", { length: 255 }).primaryKey(),
  syncToken: text("sync_token"),
  channelId: varchar("channel_id", { length: 255 }),
  resourceId: varchar("resource_id", { length: 255 }),
  expiration: timestamp("expiration", { withTimezone: true }),
  lastSyncedAt: timestamp("last_synced_at", { withTimezone: true }),
  errorCount: integer("error_count").default(0),
  lastError: text("last_error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertCalendarSyncStateSchema = createInsertSchema(calendarSyncState);
export type InsertCalendarSyncState = z.infer<typeof insertCalendarSyncStateSchema>;
export type CalendarSyncState = typeof calendarSyncState.$inferSelect;

// ==================== CHANGELOGS ====================
export const CHANGELOG_STATUSES = ["draft", "pending_approval", "approved", "published"] as const;
export type ChangelogStatus = (typeof CHANGELOG_STATUSES)[number];
export const changelogStatusSchema = z.enum(CHANGELOG_STATUSES);

export const changelogs = pgTable(
  "changelogs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    weekStart: date("week_start").notNull(),
    weekEnd: date("week_end").notNull(),
    generatedContent: text("generated_content").notNull(),
    editedContent: text("edited_content"),
    status: text("status").notNull().default("draft"),
    approvedBy: varchar("approved_by").references(() => employees.id, { onDelete: "set null" }),
    approvedAt: timestamp("approved_at"),
    publishedAt: timestamp("published_at"),
    slackMessageTs: text("slack_message_ts"),
    slackChannel: text("slack_channel"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    idxChangelogStatus: index("idx_changelogs_status").on(table.status),
    idxChangelogWeek: index("idx_changelogs_week").on(table.weekStart, table.weekEnd),
  }),
);

export const changelogsRelations = relations(changelogs, ({ one }) => ({
  approver: one(employees, {
    fields: [changelogs.approvedBy],
    references: [employees.id],
  }),
}));

export const insertChangelogSchema = createInsertSchema(changelogs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  approvedBy: true,
  approvedAt: true,
  publishedAt: true,
});

export type InsertChangelog = z.infer<typeof insertChangelogSchema>;
export type Changelog = typeof changelogs.$inferSelect;

// ==================== SALARY TABLES ====================
export const salaryTables = pgTable("salary_tables", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  fxRate: decimal("fx_rate", { precision: 8, scale: 4 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const salaryTableBands = pgTable("salary_table_bands", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  salaryTableId: varchar("salary_table_id")
    .references(() => salaryTables.id, { onDelete: "cascade" })
    .notNull(),
  key: text("key").notNull(),
  minValue: integer("min_value").notNull(),
  midValue: integer("mid_value").notNull(),
  maxValue: integer("max_value").notNull(),
  sopMeio: integer("sop_meio").notNull().default(0),
  equityMax: integer("equity_max"),
  allowedAmplitude: integer("allowed_amplitude").notNull().default(0),
  position: integer("position").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const salaryTablesRelations = relations(salaryTables, ({ many }) => ({
  bands: many(salaryTableBands),
}));

export const salaryTableBandsRelations = relations(salaryTableBands, ({ one }) => ({
  salaryTable: one(salaryTables, {
    fields: [salaryTableBands.salaryTableId],
    references: [salaryTables.id],
  }),
}));

export const insertSalaryTableSchema = createInsertSchema(salaryTables).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSalaryTable = z.infer<typeof insertSalaryTableSchema>;
export type SalaryTable = typeof salaryTables.$inferSelect;

export const insertSalaryTableBandSchema = createInsertSchema(salaryTableBands).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSalaryTableBand = z.infer<typeof insertSalaryTableBandSchema>;
export type SalaryTableBand = typeof salaryTableBands.$inferSelect;

// ==================== CONSTANTS ====================
export const OFFER_STATUSES = [
  "waiting_approval",
  "approved",
  "rejected",
  "sent",
  "accepted",
  "declined",
  "cancelled",
] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

// ==================== ANALYTICS VIEWS ====================

// Define AnalyticsDimension BEFORE the table so the .$type<>() call below can reference it
export type AnalyticsDimension = { type: "stages" } | { type: "property"; propertyId: string };

export const analyticsChartType = pgEnum("analytics_chart_type", ["bar", "pie"]);

export const jobOpeningViews = pgTable("job_opening_views", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobOpeningId: varchar("job_opening_id")
    .notNull()
    .references(() => jobOpenings.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  chartType: analyticsChartType("chart_type").notNull().default("bar"),
  // { type: 'stages' } | { type: 'property'; propertyId: string }
  primaryDimension: jsonb("primary_dimension").$type<AnalyticsDimension>().notNull(),
  // null = no secondary grouping (SQL NULL, not JSON null)
  secondaryDimension: jsonb("secondary_dimension").$type<AnalyticsDimension | null>(),
  order: integer("order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const jobOpeningViewsRelations = relations(jobOpeningViews, ({ one }) => ({
  jobOpening: one(jobOpenings, {
    fields: [jobOpeningViews.jobOpeningId],
    references: [jobOpenings.id],
  }),
}));

export type AnalyticsView = typeof jobOpeningViews.$inferSelect;
export type InsertAnalyticsView = typeof jobOpeningViews.$inferInsert;

export const insertAnalyticsViewSchema = createInsertSchema(jobOpeningViews).omit({
  id: true,
  createdAt: true,
});

export const updateAnalyticsViewSchema = insertAnalyticsViewSchema.partial().omit({
  jobOpeningId: true,
});
export type UpdateAnalyticsView = z.infer<typeof updateAnalyticsViewSchema>;

// ==================== JOB FAVORITES ====================

export const jobFavorites = pgTable(
  "job_favorites",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    employeeId: varchar("employee_id")
      .references(() => employees.id, { onDelete: "cascade" })
      .notNull(),
    jobOpeningId: varchar("job_opening_id")
      .references(() => jobOpenings.id, { onDelete: "cascade" })
      .notNull(),
    pinnedToSidebar: boolean("pinned_to_sidebar").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    uniqueEmployeeJob: unique().on(table.employeeId, table.jobOpeningId),
  }),
);

export const jobFavoritesRelations = relations(jobFavorites, ({ one }) => ({
  employee: one(employees, {
    fields: [jobFavorites.employeeId],
    references: [employees.id],
  }),
  jobOpening: one(jobOpenings, {
    fields: [jobFavorites.jobOpeningId],
    references: [jobOpenings.id],
  }),
}));

export const insertJobFavoriteSchema = createInsertSchema(jobFavorites).omit({
  id: true,
  createdAt: true,
});

export type JobFavorite = typeof jobFavorites.$inferSelect;
export type InsertJobFavorite = z.infer<typeof insertJobFavoriteSchema>;

// ==================== SCORING CONFIG ====================
export const scoringConfig = pgTable("scoring_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  section: text("section").unique().notNull(),
  data: jsonb("data").notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: varchar("updated_by"),
});

// ==================== LABS · CHRO CALIBRATION ====================
// `assessment_id` is not an FK yet — the `candidate_assessments` table does not exist
// in Phase 1 (see design doc Open Question #1). Tighten to an FK once it lands.
export const candidateCorrections = pgTable(
  "candidate_corrections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    assessmentId: uuid("assessment_id").notNull(),
    evidenceId: text("evidence_id").notNull(),
    originalScore: numeric("original_score").notNull(),
    correctedScore: numeric("corrected_score").notNull(),
    rationale: text("rationale").notNull(),
    correctedBy: text("corrected_by").notNull(),
    correctedAt: timestamp("corrected_at", { withTimezone: true }).notNull().defaultNow(),
    // Lifecycle: pending | parked | running | applied | awaiting_review | rejected | reverted | conflict | failed
    status: text("status").notNull().default("pending"),
    agentPatch: text("agent_patch"),
    validationReport: jsonb("validation_report"),
    appliedAt: timestamp("applied_at", { withTimezone: true }),
    // Commit policy — "auto" applies on zero regressions, "preview" parks in
    // awaiting_review for CHRO confirmation.
    policy: text("policy").notNull().default("auto"),
    reviewedBy: text("reviewed_by"),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    // Routing for the calibration agent. `dim` is the legacy evidence-prompt path.
    // axis_weights/axis_threshold/skill_leveling/skill_framework target JSON or
    // prose prompt sections owned by the addendum. `target_ref` holds the axis id,
    // skill slug, or dim slug depending on kind; null for skill_leveling.
    targetKind: text("target_kind").notNull().default("dim"),
    targetRef: text("target_ref"),
    // Snapshot of the scoring_config section's `data` taken immediately before the
    // patch was applied. Null for legacy rows applied before revert landed.
    // Revert restores this value via upsertScoringConfig.
    priorData: jsonb("prior_data"),
    revertedBy: text("reverted_by"),
    revertedAt: timestamp("reverted_at", { withTimezone: true }),
    // Frozen copy of the three-axis assessment payload at the moment the
    // CHRO submitted this correction. The calibration agent rescores the
    // candidate against the patched prompt using THIS profile, so the input
    // matches what the CHRO was looking at — even if the row sits in the
    // parked queue for hours/days before a drain picks it up. Replaces an
    // earlier in-memory registry that evicted after 10 min and was empty
    // for park-policy rows entirely, which made the model rescore an empty
    // profile and always return score=1.0. Null for legacy rows submitted
    // before this column existed.
    candidateProfileSnapshot: jsonb("candidate_profile_snapshot"),
  },
  (t) => ({
    evidenceIdx: index("idx_corrections_evidence").on(t.evidenceId),
    statusIdx: index("idx_corrections_status").on(t.status),
  }),
);

export const calibrationFixtures = pgTable("calibration_fixtures", {
  id: uuid("id").primaryKey().defaultRandom(),
  candidateName: text("candidate_name").notNull().unique(),
  profile: jsonb("profile").notNull(),
  /** Per-evidence (dim) expected scores: { academic: 3, tenure: 2.5, ... }.
   *  These are the "what GCA/CompWay should evaluate this candidate at"
   *  targets the calibration agent tries to preserve. */
  expectedScores: jsonb("expected_scores").notNull(),
  /** Per-skill expected dot counts: { project_management: 4, ... }. Stored
   *  alongside the evidence scores so the calibration agent can also honor
   *  the skills axis during re-runs — not just the dim-level rubric. Empty
   *  object for fixtures seeded before the column existed. */
  expectedSkills: jsonb("expected_skills").notNull().default(sql`'{}'::jsonb`),
  /** Discipline this fixture belongs to. Defaults to 'forward-deployed' for
   *  all existing rows. Used to scope fixture validation to the discipline
   *  of the section being patched during batch calibration. */
  discipline: text("discipline").notNull().default("forward-deployed"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

/**
 * Append-only history for calibration-critical records. Every write to
 * `scoring_config` or `calibration_fixtures` snapshots its payload here so
 * the CHRO can inspect past states and roll back if a calibration drift
 * slips in. Rollback itself is implemented as a NEW insert whose content
 * equals an older version's — no row is ever mutated or deleted.
 *
 *   kind          — 'scoring_config' | 'calibration_fixture'
 *   identifier    — scoring_config.section or calibration_fixture.candidate_name
 *   versionNumber — monotonic per (kind, identifier), 1-indexed
 */
export const configVersions = pgTable(
  "config_versions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    kind: text("kind").notNull(),
    identifier: text("identifier").notNull(),
    versionNumber: integer("version_number").notNull(),
    content: jsonb("content").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    createdBy: text("created_by"),
    reason: text("reason"),
  },
  (t) => ({
    uniqKindIdVersion: unique().on(t.kind, t.identifier, t.versionNumber),
    lookupIdx: index("config_versions_lookup").on(t.kind, t.identifier, t.versionNumber),
  }),
);

/**
 * Archive of completed candidate evaluations. Each row captures the profile
 * payload + full assessment + any corrections the CHRO made, so reviewers
 * can come back weeks later and see exactly what was decided and why.
 *
 * The `candidate_name` is stored raw for operational debugging but never
 * leaves the server; the list and detail endpoints return a display-only
 * "Perfil A/B/…" alias derived from the insertion order (same convention as
 * the calibration corpus browser).
 */
export const labsEvaluations = pgTable(
  "labs_evaluations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    assessmentId: text("assessment_id"),
    candidateName: text("candidate_name"),
    disciplineSlug: text("discipline_slug"),
    targetLevel: text("target_level"),
    profile: jsonb("profile").notNull(),
    assessment: jsonb("assessment").notNull(),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    createdAtIdx: index("labs_evaluations_created_at").on(t.createdAt),
  }),
);

/**
 * Append-only log of every LLM call the labs surface makes. Lets the
 * engineering view track cost per profile evaluation and per calibration
 * run, surface rolling totals, and drill into outlier corrections.
 *
 * Writes are best-effort — the recorder catches its own errors so a logging
 * blip never breaks an evaluation. Pruning is intentionally NOT automatic;
 * a follow-up cron can prune rows older than N days when retention becomes
 * a concern.
 */
export const labsLlmCosts = pgTable(
  "labs_llm_costs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    /** Coarse category: "evaluation" | "calibration" | "rerun" | "baseline". */
    surface: text("surface").notNull(),
    /** Specific call inside the surface ("call1-classification",
     *  "rescore-tenure", "patch-proposer"). Used for breakdown charts. */
    subject: text("subject").notNull(),
    /** Optional FK-ish reference: the labs_evaluations.id for an evaluation
     *  call, candidate_corrections.id for a calibration call, or run id for
     *  a baseline call. Null for one-offs. Stored as text so we can carry
     *  any of the three id formats without schema gymnastics. */
    correlationId: text("correlation_id"),
    /** Concrete model name as routed (e.g. claude-3-5-haiku-20241022). */
    model: text("model").notNull(),
    /** Raw token counts as reported by the provider via response.usage. */
    promptTokens: integer("prompt_tokens").notNull().default(0),
    completionTokens: integer("completion_tokens").notNull().default(0),
    /** Tokens served from prompt cache (Anthropic prompt-caching). Counted
     *  separately so the cost calculation can apply the cache discount. */
    cachedInputTokens: integer("cached_input_tokens").notNull().default(0),
    /** Computed at insert time using the per-model pricing map in
     *  server/labs/llm-pricing.ts. numeric to keep decimal precision. */
    costUsd: numeric("cost_usd").notNull().default("0"),
    /** Wall-time of the LLM call, in ms. */
    latencyMs: integer("latency_ms").notNull().default(0),
    /** Email of the actor who triggered the chain that produced this call,
     *  when known. Null for anonymous / system-triggered calls. */
    actor: text("actor"),
  },
  (t) => ({
    createdAtIdx: index("labs_llm_costs_created_at").on(t.createdAt),
    surfaceSubjectIdx: index("labs_llm_costs_surface_subject").on(t.surface, t.subject),
    correlationIdx: index("labs_llm_costs_correlation").on(t.correlationId),
  }),
);

export type CandidateCorrection = typeof candidateCorrections.$inferSelect;
export type InsertCandidateCorrection = typeof candidateCorrections.$inferInsert;
export type CalibrationFixture = typeof calibrationFixtures.$inferSelect;
export type InsertCalibrationFixture = typeof calibrationFixtures.$inferInsert;
export type ConfigVersion = typeof configVersions.$inferSelect;
export type InsertConfigVersion = typeof configVersions.$inferInsert;
export type LabsEvaluation = typeof labsEvaluations.$inferSelect;
export type InsertLabsEvaluation = typeof labsEvaluations.$inferInsert;
export type LabsLlmCost = typeof labsLlmCosts.$inferSelect;
export type InsertLabsLlmCost = typeof labsLlmCosts.$inferInsert;

export type ScoringConfig = typeof scoringConfig.$inferSelect;

// ==================== SKILLS REGISTRY ====================
// Generic prompt/skill registry. Metadata lives here; prompt bodies live in
// the object store under `skills/<skillId>/v<n>.txt` (see services/skill-prompt-store).
//
// Invariant: the circular FK skills.published_version_id → skill_versions.id
// is NOT marked DEFERRABLE. This is safe only because no code path ever
// inserts a skill row and a version row in the same statement — the workflow
// is always: insert skill (publishedVersionId=null) → insert version → UPDATE
// skill on publish. Don't introduce single-statement multi-row inserts that
// require deferred FK checking.

export const SKILL_VERSION_STATUSES = ["draft", "published", "archived"] as const;
export type SkillVersionStatus = (typeof SKILL_VERSION_STATUSES)[number];

export const SKILL_EXECUTION_STATUSES = ["succeeded", "failed"] as const;
export type SkillExecutionStatus = (typeof SKILL_EXECUTION_STATUSES)[number];

export const SKILL_FAILURE_STAGES = ["input-validation", "llm", "output-validation"] as const;
export type SkillFailureStage = (typeof SKILL_FAILURE_STAGES)[number];

export const skillGroups = pgTable("skill_groups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const skills = pgTable("skills", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  groupId: varchar("group_id")
    .references(() => skillGroups.id, { onDelete: "restrict" })
    .notNull(),
  description: text("description"),
  inputSchema: jsonb("input_schema").notNull(),
  outputSchema: jsonb("output_schema").notNull(),
  defaultModel: text("default_model").notNull(),
  defaultTemperature: decimal("default_temperature", { precision: 3, scale: 2 })
    .default("0.20")
    .notNull(),
  defaultTags: jsonb("default_tags").$type<string[]>(),
  // Circular ref → skillVersions.id; lazy via AnyPgColumn. Nullable until first publish.
  publishedVersionId: varchar("published_version_id").references(
    (): AnyPgColumn => skillVersions.id,
    { onDelete: "set null" },
  ),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const skillVersions = pgTable(
  "skill_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    skillId: varchar("skill_id")
      .references(() => skills.id, { onDelete: "cascade" })
      .notNull(),
    version: integer("version").notNull(),
    // 'draft' | 'published' | 'archived'
    status: text("status").notNull(),
    objectStoreKey: text("object_store_key").notNull(),
    createdBy: varchar("created_by").references(() => employees.id, { onDelete: "set null" }),
    publishedAt: timestamp("published_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => [
    unique("skill_versions_skill_version_unique").on(t.skillId, t.version),
    // At most one published version per skill.
    uniqueIndex("skill_versions_one_published_per_skill")
      .on(t.skillId)
      .where(sql`status = 'published'`),
  ],
);

export const skillExecutions = pgTable("skill_executions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  skillId: varchar("skill_id").references(() => skills.id, { onDelete: "set null" }),
  versionId: varchar("version_id").references(() => skillVersions.id, { onDelete: "set null" }),
  // 'succeeded' | 'failed'
  status: text("status").notNull(),
  // 'input-validation' | 'llm' | 'output-validation' (only set on failures)
  failureStage: text("failure_stage"),
  errorMessage: text("error_message"),
  latencyMs: integer("latency_ms"),
  tags: jsonb("tags").$type<string[]>(),
  costUsd: decimal("cost_usd", { precision: 10, scale: 6 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const skillGroupsRelations = relations(skillGroups, ({ many }) => ({
  skills: many(skills),
}));

export const skillsRelations = relations(skills, ({ one, many }) => ({
  group: one(skillGroups, {
    fields: [skills.groupId],
    references: [skillGroups.id],
  }),
  publishedVersion: one(skillVersions, {
    fields: [skills.publishedVersionId],
    references: [skillVersions.id],
    relationName: "skill_published_version",
  }),
  versions: many(skillVersions, { relationName: "skill_versions_for_skill" }),
  executions: many(skillExecutions),
}));

export const skillVersionsRelations = relations(skillVersions, ({ one, many }) => ({
  skill: one(skills, {
    fields: [skillVersions.skillId],
    references: [skills.id],
    relationName: "skill_versions_for_skill",
  }),
  createdByEmployee: one(employees, {
    fields: [skillVersions.createdBy],
    references: [employees.id],
  }),
  executions: many(skillExecutions),
}));

export const skillExecutionsRelations = relations(skillExecutions, ({ one }) => ({
  skill: one(skills, {
    fields: [skillExecutions.skillId],
    references: [skills.id],
  }),
  version: one(skillVersions, {
    fields: [skillExecutions.versionId],
    references: [skillVersions.id],
  }),
}));

export type SkillGroup = typeof skillGroups.$inferSelect;
export type InsertSkillGroup = typeof skillGroups.$inferInsert;
export type Skill = typeof skills.$inferSelect;
export type InsertSkill = typeof skills.$inferInsert;
export type SkillVersion = typeof skillVersions.$inferSelect;
export type InsertSkillVersion = typeof skillVersions.$inferInsert;
export type SkillExecution = typeof skillExecutions.$inferSelect;
export type InsertSkillExecution = typeof skillExecutions.$inferInsert;

// ==================== ONBOARDING REFACTOR (BATCH 0) ====================
// New tables introduced by refactor/onboarding-rewrite. Old tables
// (onboarding_template_subsections, _channels, _tools, _milestones,
// slack_auto_join_jobs) coexist dead until final cleanup batch.

export const MILESTONE_DAYS = [1, 2, 3, 15, 30, 60] as const;
export type MilestoneDay = (typeof MILESTONE_DAYS)[number];

export const assignmentScope = pgEnum("assignment_scope", ["empresa", "disciplina"]);
export type AssignmentScope = (typeof assignmentScope.enumValues)[number];

export const resourceAuditAction = pgEnum("resource_audit_action", [
  "created",
  "updated",
  "archived",
  "restored",
  "deleted",
  "content_changed",
  "render_type_changed",
  "source_attached",
  "progress_trackable_changed",
  "resynced",
]);
export type ResourceAuditAction = (typeof resourceAuditAction.enumValues)[number];

// --- TEMPLATE SIDE ---

export const templateResourceAssignments = pgTable(
  "template_resource_assignments",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "cascade" })
      .notNull(),
    resourceId: varchar("resource_id")
      .references(() => onboardingResources.id, { onDelete: "cascade" })
      .notNull(),
    milestoneDay: smallint("milestone_day").notNull(),
    scope: assignmentScope("scope").notNull(),
    position: integer("position").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("template_resource_assignments_template_resource_milestone_unique").on(
      table.templateId,
      table.resourceId,
      table.milestoneDay,
    ),
    index("idx_template_resource_assignments_template_milestone").on(
      table.templateId,
      table.milestoneDay,
    ),
    index("idx_template_resource_assignments_resource").on(table.resourceId),
  ],
);

export const templateResourceAssignmentsRelations = relations(
  templateResourceAssignments,
  ({ one }) => ({
    template: one(onboardingTemplates, {
      fields: [templateResourceAssignments.templateId],
      references: [onboardingTemplates.id],
    }),
    resource: one(onboardingResources, {
      fields: [templateResourceAssignments.resourceId],
      references: [onboardingResources.id],
    }),
  }),
);

export type TemplateResourceAssignment = typeof templateResourceAssignments.$inferSelect;
export type InsertTemplateResourceAssignment = typeof templateResourceAssignments.$inferInsert;

export const templateSidebarSections = pgTable(
  "template_sidebar_sections",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    templateId: varchar("template_id")
      .references(() => onboardingTemplates.id, { onDelete: "cascade" })
      .notNull(),
    label: text("label").notNull(),
    position: integer("position").notNull().default(0),
    isFixed: boolean("is_fixed").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("template_sidebar_sections_template_label_unique").on(table.templateId, table.label),
    index("idx_template_sidebar_sections_template_position").on(table.templateId, table.position),
  ],
);

export const templateSidebarSectionsRelations = relations(
  templateSidebarSections,
  ({ one, many }) => ({
    template: one(onboardingTemplates, {
      fields: [templateSidebarSections.templateId],
      references: [onboardingTemplates.id],
    }),
    items: many(templateSidebarItems),
  }),
);

export type TemplateSidebarSection = typeof templateSidebarSections.$inferSelect;
export type InsertTemplateSidebarSection = typeof templateSidebarSections.$inferInsert;

export const templateSidebarItems = pgTable(
  "template_sidebar_items",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sectionId: varchar("section_id")
      .references(() => templateSidebarSections.id, { onDelete: "cascade" })
      .notNull(),
    resourceId: varchar("resource_id")
      .references(() => onboardingResources.id, { onDelete: "cascade" })
      .notNull(),
    position: integer("position").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    unique("template_sidebar_items_section_resource_unique").on(table.sectionId, table.resourceId),
    index("idx_template_sidebar_items_section_position").on(table.sectionId, table.position),
  ],
);

export const templateSidebarItemsRelations = relations(templateSidebarItems, ({ one }) => ({
  section: one(templateSidebarSections, {
    fields: [templateSidebarItems.sectionId],
    references: [templateSidebarSections.id],
  }),
  resource: one(onboardingResources, {
    fields: [templateSidebarItems.resourceId],
    references: [onboardingResources.id],
  }),
}));

export type TemplateSidebarItem = typeof templateSidebarItems.$inferSelect;
export type InsertTemplateSidebarItem = typeof templateSidebarItems.$inferInsert;

// --- ONBOARDING SNAPSHOT SIDE (fork-at-start, BR-G-018) ---

export const onboardingAssignments = pgTable(
  "onboarding_assignments",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    resourceId: varchar("resource_id")
      .references(() => onboardingResources.id, { onDelete: "cascade" })
      .notNull(),
    milestoneDay: smallint("milestone_day").notNull(),
    scope: assignmentScope("scope").notNull(),
    position: integer("position").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("onboarding_assignments_onboarding_resource_milestone_unique").on(
      table.onboardingId,
      table.resourceId,
      table.milestoneDay,
    ),
    index("idx_onboarding_assignments_onboarding_milestone").on(
      table.onboardingId,
      table.milestoneDay,
    ),
    index("idx_onboarding_assignments_resource").on(table.resourceId),
  ],
);

export const onboardingAssignmentsRelations = relations(onboardingAssignments, ({ one }) => ({
  onboarding: one(onboardings, {
    fields: [onboardingAssignments.onboardingId],
    references: [onboardings.id],
  }),
  resource: one(onboardingResources, {
    fields: [onboardingAssignments.resourceId],
    references: [onboardingResources.id],
  }),
}));

export type OnboardingAssignment = typeof onboardingAssignments.$inferSelect;
export type InsertOnboardingAssignment = typeof onboardingAssignments.$inferInsert;

export const onboardingSidebarSections = pgTable(
  "onboarding_sidebar_sections",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    onboardingId: varchar("onboarding_id")
      .references(() => onboardings.id, { onDelete: "cascade" })
      .notNull(),
    label: text("label").notNull(),
    position: integer("position").notNull().default(0),
    isFixed: boolean("is_fixed").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("onboarding_sidebar_sections_onboarding_label_unique").on(
      table.onboardingId,
      table.label,
    ),
    index("idx_onboarding_sidebar_sections_onboarding_position").on(
      table.onboardingId,
      table.position,
    ),
  ],
);

export const onboardingSidebarSectionsRelations = relations(
  onboardingSidebarSections,
  ({ one, many }) => ({
    onboarding: one(onboardings, {
      fields: [onboardingSidebarSections.onboardingId],
      references: [onboardings.id],
    }),
    items: many(onboardingSidebarItems),
  }),
);

export type OnboardingSidebarSection = typeof onboardingSidebarSections.$inferSelect;
export type InsertOnboardingSidebarSection = typeof onboardingSidebarSections.$inferInsert;

export const onboardingSidebarItems = pgTable(
  "onboarding_sidebar_items",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sectionId: varchar("section_id")
      .references(() => onboardingSidebarSections.id, { onDelete: "cascade" })
      .notNull(),
    resourceId: varchar("resource_id")
      .references(() => onboardingResources.id, { onDelete: "cascade" })
      .notNull(),
    position: integer("position").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    unique("onboarding_sidebar_items_section_resource_unique").on(
      table.sectionId,
      table.resourceId,
    ),
    index("idx_onboarding_sidebar_items_section_position").on(table.sectionId, table.position),
  ],
);

export const onboardingSidebarItemsRelations = relations(onboardingSidebarItems, ({ one }) => ({
  section: one(onboardingSidebarSections, {
    fields: [onboardingSidebarItems.sectionId],
    references: [onboardingSidebarSections.id],
  }),
  resource: one(onboardingResources, {
    fields: [onboardingSidebarItems.resourceId],
    references: [onboardingResources.id],
  }),
}));

export type OnboardingSidebarItem = typeof onboardingSidebarItems.$inferSelect;
export type InsertOnboardingSidebarItem = typeof onboardingSidebarItems.$inferInsert;

// --- RESOURCE AUDIT LOG (BR-G-020) ---

export const resourceAuditLog = pgTable(
  "resource_audit_log",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    resourceId: varchar("resource_id")
      .references(() => onboardingResources.id, { onDelete: "cascade" })
      .notNull(),
    actorClerkId: text("actor_clerk_id").notNull(),
    action: resourceAuditAction("action").notNull(),
    before: jsonb("before").$type<Record<string, unknown> | null>(),
    after: jsonb("after").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_resource_audit_log_resource_created_at").on(table.resourceId, table.createdAt),
  ],
);

export const resourceAuditLogRelations = relations(resourceAuditLog, ({ one }) => ({
  resource: one(onboardingResources, {
    fields: [resourceAuditLog.resourceId],
    references: [onboardingResources.id],
  }),
}));

export type ResourceAuditLogEntry = typeof resourceAuditLog.$inferSelect;
export type InsertResourceAuditLogEntry = typeof resourceAuditLog.$inferInsert;

// ==================== USER PERMISSIONS ====================
// Granular per-user permissions. Admin (users.is_super_admin) bypasses this
// table entirely and isManager is a dynamic runtime bypass — neither has rows
// here. Rows exist only for users explicitly granted access to a feature.
export const PERMISSION_KEYS = [
  "employees",
  "employees_manager",
  "org_chart",
  "onboarding",
  "buddy_pool",
  "performance",
  "surveys",
  "surveys_manager",
  "salary_tables",
  "recruiting",
  "labs_intel",
  "labs_feedback",
  "approve_jobs_offers",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const userPermissions = pgTable(
  "user_permissions",
  {
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    permission: varchar("permission", { length: 64 }).notNull(),
    grantedAt: timestamp("granted_at").defaultNow(),
    grantedBy: varchar("granted_by").references(() => users.id),
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.permission] }),
    index("idx_user_permissions_permission").on(table.permission),
  ],
);

export const userPermissionsRelations = relations(userPermissions, ({ one }) => ({
  user: one(users, { fields: [userPermissions.userId], references: [users.id] }),
  granter: one(users, { fields: [userPermissions.grantedBy], references: [users.id] }),
}));

export type UserPermission = typeof userPermissions.$inferSelect;
export type InsertUserPermission = typeof userPermissions.$inferInsert;

// ==================== LEVEL ASSESSMENT (multi-model) ====================
//
// Persistence for the level-assessment feature. Before: every model lived on
// the filesystem under docs/labs/models/<id>/ — model.json, signal-matrix.json,
// per-axis rubric and prompt markdowns, plus calibration JSONLs. Replit
// Autoscale wipes the runtime FS between deploys, so user-created models +
// calibration corpus were silently lost on every deploy.
//
// The seed (`comp-universal`) still lives in git as the initial content; a
// startup hook hydrates it into the DB the first time the server boots
// against a fresh database. After that, DB is the source of truth — UI
// edits to the seed survive deploys.

/** One evaluation model. The seed (`comp-universal`) is inserted by the
 *  startup hook from filesystem; user-created models are inserted by the
 *  create-model wizard. `signal_matrix` is nullable because new models
 *  exist briefly between "create" and "bootstrap completes" — the LLM
 *  bootstrap fills it in. */
export const levelAssessmentModels = pgTable("level_assessment_models", {
  /** Stable slug used as the directory name in the legacy FS layout. The
   *  primary key — references from rubric/prompt/calibration tables target
   *  this column with ON DELETE CASCADE. */
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull().default(""),
  /** Model-wide ("cross") competencies: ordered list of `{ id, label,
   *  description }` — order is part of the contract because UI components
   *  (matrix columns, axis cards) iterate in this order. Vertical (discipline)
   *  competencies live in `level_assessment_model_disciplines`. */
  crossAxes: jsonb("cross_axes").notNull(),
  /** `{ min, max }` — inclusive range. Validated at the usecase boundary
   *  against the level numbers in signal_matrix and per-axis rubric/prompts. */
  levels: jsonb("levels").notNull(),
  /** Scoped signal-key → routing map. Shape: `{ cross: { signals: {...} },
   *   disciplines: { <disciplineId>: { signals: {...} } } }`. Null while the
   *  model has been created but the bootstrap LLM hasn't filled it yet. */
  signalMatrix: jsonb("signal_matrix"),
  aggregation: text("aggregation").notNull().default("mean_dispersion"),
  /** True only for `comp-universal`. Seed cannot be deleted via the API —
   *  the empty-state backstop. */
  isSeed: boolean("is_seed").notNull().default(false),
  /** Bumped whenever any axis's rubric content changes. The UI flags the
   *  model as "stale" when this is greater than `last_bootstrap_at` so the
   *  user knows to re-bootstrap. */
  rubricUpdatedAt: timestamp("rubric_updated_at").notNull().defaultNow(),
  /** Set by the bootstrap orchestrator on full success; cleared (kept as the
   *  previous timestamp) on partial failure. Null when never bootstrapped. */
  lastBootstrapAt: timestamp("last_bootstrap_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

/** Disciplines within a model — each owns a set of vertical competencies.
 *  The axes themselves live in this row's `axes` jsonb; the discipline's
 *  rubrics/prompts are keyed by `scope = discipline_id` in the rubric/prompt
 *  tables. Cascade-deleted with the model. */
export const levelAssessmentModelDisciplines = pgTable(
  "level_assessment_model_disciplines",
  {
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** Slug, unique within the model. Used as the `scope` segment for the
     *  discipline's rubrics/prompts and as its signal-matrix key. */
    disciplineId: text("discipline_id").notNull(),
    label: text("label").notNull(),
    description: text("description").notNull().default(""),
    /** Ordered list of `{ id, label, description }` vertical competencies. */
    axes: jsonb("axes").notNull(),
    /** Optional `{ min, max }` continuous sub-band of the model's level range
     *  this function spans (e.g. L3–L6 in an L0–L7 model). Null = the full
     *  model range. Out-of-band levels are auto-filled in the discipline's
     *  rubric so prompts understand the boundary. */
    levels: jsonb("levels"),
    order: integer("order").notNull().default(0),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.modelId, table.disciplineId] })],
);

/** Per-axis rubric markdown. One row per (model, scope, axis). `scope` is
 *  `cross` for model-wide competencies or a `discipline_id` for vertical
 *  competencies. Source of truth for the per-(axis, level) cells the matrix
 *  editor renders; the markdown is reconstructed by the create/edit usecase
 *  from the cell map. */
export const levelAssessmentModelRubrics = pgTable(
  "level_assessment_model_rubrics",
  {
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** `cross` | `<disciplineId>` — which scope's axis set this rubric belongs
     *  to. */
    scope: text("scope").notNull().default("cross"),
    /** Matches an axis id within the scope's axis set. Not enforced by FK
     *  because the axes are jsonb; consistency is the usecase's job. */
    axisId: text("axis_id").notNull(),
    /** The level descriptions (Pillar 2), rendered as `- **L{n}** — …` bullets.
     *  Still the canonical source for the 3-parser contract + explainability +
     *  the verbatim `<rubric>` block in scoring prompts. */
    content: text("content").notNull(),
    /** Optional structured rater-training fields for this axis
     *  (`StructuredRubricFieldsSchema`): per-cell what-counts / easy-to-miss /
     *  reference profiles + per-adjacent-pair "crosses-when" boundaries. Null
     *  for legacy + seed rubrics — the structured-block renderer then emits
     *  nothing extra, so behaviour is byte-identical to a blob-only rubric.
     *  The per-axis pillars (notAbout, baseline, falsePositives, hardCap,
     *  cautionaryProfiles) live on the axis JSONB, not here. */
    fields: jsonb("fields"),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.modelId, table.scope, table.axisId] })],
);

/** Bootstrapped or manually-edited prompts. One row per (model, kind).
 *  `kind` is `axis:cross:<axisId>` (cross competencies) or
 *  `axis:<disciplineId>:<axisId>` (vertical competencies). Kept as a single
 *  table (not three) so future kinds don't need new tables. */
export const levelAssessmentModelPrompts = pgTable(
  "level_assessment_model_prompts",
  {
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** `axis:cross:<axisId>` | `axis:<disciplineId>:<axisId>`. The bootstrap
     *  orchestrator and the runtime LLM services agree on this vocabulary. */
    kind: text("kind").notNull(),
    content: text("content").notNull(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.modelId, table.kind] })],
);

/** Calibration corpus — one ground-truth datapoint per (profile × axis).
 *
 *  Collected at the end of an evaluation: the operator reviews each axis
 *  score the model produced and either CONFIRMS it (agreed = true, the
 *  corrected level equals the model's level) or DISAGREES and supplies a
 *  corrected level (agreed = false). Nothing is collected implicitly — only
 *  axes the operator explicitly reviewed produce a row.
 *
 *  Optimization runs at the axis grain, so the corpus is stored at the axis
 *  grain too. Each row snapshots the raw LinkedIn profile + the extracted
 *  feature vector so a calibration run can replay the per-axis engine against
 *  the exact evidence the score was based on, even if the profile or the
 *  extractors change later. */
export const levelAssessmentAxisSamples = pgTable(
  "level_assessment_axis_samples",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** Discipline of a vertical axis — null for a cross axis. */
    disciplineId: text("discipline_id"),
    /** The axis this datapoint scores (e.g. `autonomy`, `project_management`). */
    axisId: text("axis_id").notNull(),
    /** `cross` | `vertical` — which section the axis belongs to. */
    scope: text("scope").notNull(),
    linkedinUrl: text("linkedin_url"),
    candidateName: text("candidate_name"),
    /** Stable key (sha256 of the raw profile) used to dedupe: a re-review of
     *  the same profile/axis replaces the prior row instead of accumulating. */
    profileKey: text("profile_key").notNull(),
    /** Apify snapshot at review time — PII (raw LinkedIn JSON). */
    rawProfile: jsonb("raw_profile").notNull(),
    /** Extracted feature vector at review time — the per-axis engine's input. */
    features: jsonb("features").notNull(),
    /** Signal-suite version that produced `features` (null = legacy/pre-
     *  versioning). Calibration re-extracts any sample whose version is behind
     *  the current suite so optimization runs on current evidence, not the
     *  signal set that existed when the review was collected. */
    signalsVersion: integer("signals_version"),
    /** The level the model inferred for this axis (null when it abstained). */
    modelLevel: integer("model_level"),
    /** The model's confidence for this axis, 0–1 (null when unavailable). */
    modelConfidence: doublePrecision("model_confidence"),
    /** Ground truth: equals `modelLevel` when agreed, else the operator's
     *  corrected level. */
    correctedLevel: integer("corrected_level").notNull(),
    /** Whether the operator confirmed the model's score (true) or overrode
     *  it (false). */
    agreed: boolean("agreed").notNull(),
    note: text("note"),
    /** Email of the reviewer (or null for anonymous). */
    actor: text("actor"),
    /** Provenance. `review` = an operator-reviewed datapoint — the calibration
     *  corpus GEPA optimizes against. `anchor` = an author-curated matrix
     *  reference profile, extracted to signals and baked into scoring prompts
     *  as a worked example. Anchors are TRAINING material and are EXCLUDED from
     *  the GEPA eval/Pareto set + the calibration-readiness counts to prevent
     *  train/test leakage. Defaults to `review` so existing rows are unchanged. */
    source: text("source").notNull().default("review"),
    /** Evaluation set this review originated from — PROVENANCE ONLY. The sample
     *  stays `source = 'review'`, so GEPA/readiness/coverage treat it identically
     *  to a single-evaluation review; this column only records WHERE the review
     *  came from, for auditability in the calibration data hub. `set null` on set
     *  delete so deleting a set never drops ground-truth samples. */
    evaluationSetId: uuid("evaluation_set_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentEvaluationSets.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_level_assessment_axis_samples_model_axis_created").on(
      table.modelId,
      table.axisId,
      table.createdAt,
    ),
  ],
);

/** Calibration runs — one row per time an operator triggers the optimization
 *  procedure for a single axis. Powers the "last calibration date" + history
 *  in the calibration command center. The run's outcome is a proposed prompt
 *  + an accuracy report; the optimization algorithm that fills them is defined
 *  separately. */
export const levelAssessmentAxisCalibrationRuns = pgTable(
  "level_assessment_axis_calibration_runs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    disciplineId: text("discipline_id"),
    axisId: text("axis_id").notNull(),
    scope: text("scope").notNull(),
    /** `running` | `done` | `error`. */
    status: text("status").notNull().default("running"),
    /** How many samples fed the run (snapshot at start). */
    sampleCount: integer("sample_count").notNull().default(0),
    /** Accuracy report produced by the run (shape owned by the optimizer). */
    accuracyReport: jsonb("accuracy_report"),
    /** Prompt the run proposes for this axis — null until the optimizer fills it. */
    proposedPrompt: text("proposed_prompt"),
    /** Whether the operator accepted the proposed prompt. */
    accepted: boolean("accepted").notNull().default(false),
    errorMessage: text("error_message"),
    /** Calibration session that auto-triggered this run — null for a manually
     *  triggered run. Provenance for the workflow's "model improved" view.
     *  `set null` on session delete so a cleaned-up session never drops a run. */
    sessionId: uuid("session_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentCalibrationSessions.id,
      { onDelete: "set null" },
    ),
    actor: text("actor"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    finishedAt: timestamp("finished_at"),
  },
  (table) => [
    index("idx_level_assessment_axis_calib_runs_model_axis_created").on(
      table.modelId,
      table.axisId,
      table.createdAt,
    ),
  ],
);

/** Persisted Level Assessment runs — one row per evaluation, written on every
 *  run so it has a stable, shareable id. The row is self-contained: `result`
 *  embeds the model snapshot, so the report re-renders even after the source
 *  model is edited. `rawProfile`/`features`/`profileKey` are snapshotted so the
 *  end-of-evaluation calibration review can write samples from a shared link
 *  without a paid LinkedIn re-scrape. Cascade-deleted with the model. */
export const levelAssessmentEvaluations = pgTable(
  "level_assessment_evaluations",
  {
    /** The share id — unguessable random UUID; the link is gated to labs users. */
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** Discipline scored for this run — null for a cross-only model. */
    disciplineId: text("discipline_id"),
    candidateName: text("candidate_name").notNull(),
    /** LinkedIn headline shown as the report subtitle — null in free-text mode. */
    candidateSubtitle: text("candidate_subtitle"),
    /** Display label for the run's context (discipline label or model name). */
    contextLabel: text("context_label").notNull(),
    /** `linkedin` | `free-text` — how the candidate evidence was supplied. */
    source: text("source").notNull(),
    linkedinUrl: text("linkedin_url"),
    /** The full `LevelAssessmentResult` (two sections + overall + modelSnapshot). */
    result: jsonb("result").notNull(),
    /** The `ExtractedProfileSummary` shown in the report's profile panel. */
    extractedProfile: jsonb("extracted_profile").notNull(),
    /** Apify snapshot — PII. Null in free-text mode. Reused by the review path. */
    rawProfile: jsonb("raw_profile"),
    /** Extracted feature vector at run time — reused by the review path. */
    features: jsonb("features"),
    /** Signal-suite version that produced `features` (null = legacy). Copied
     *  onto any calibration sample collected from this evaluation's snapshot. */
    signalsVersion: integer("signals_version"),
    /** sha256 of the raw profile — aligns the review dedupe key with the run. */
    profileKey: text("profile_key"),
    /** Email of the actor who ran the evaluation (or null). */
    createdBy: text("created_by"),
    /** Calibration session this evaluation was produced for — null for an
     *  ad-hoc run. Set when the run is part of a guided calibration round so
     *  the session can enumerate its reference profiles WITHOUT changing the
     *  axis-sample dedupe grain (samples stay model-global). `set null` on
     *  session delete keeps the shareable evaluation alive. */
    sessionId: uuid("session_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentCalibrationSessions.id,
      { onDelete: "set null" },
    ),
    /** Evaluation set this run belongs to — null for an ad-hoc/single run or a
     *  calibration-session run. Parallel to `sessionId`; lets a set enumerate its
     *  candidates without touching the model-global axis-sample dedupe grain.
     *  `set null` on set delete keeps the shareable evaluation alive. */
    evaluationSetId: uuid("evaluation_set_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentEvaluationSets.id,
      { onDelete: "set null" },
    ),
    /** Tracked candidate this run belongs to — null for a one-shot/ad-hoc run.
     *  Parallel to `sessionId`/`evaluationSetId`; lets a candidate enumerate its
     *  evaluations without joining through rounds, and survives a round
     *  `set null`. Set together with `roundId` in the pipeline (never by callers
     *  independently), so `candidateId === round.candidateId` always holds.
     *  `set null` on candidate delete keeps the shareable evaluation alive. */
    candidateId: uuid("candidate_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentCandidates.id,
      { onDelete: "set null" },
    ),
    /** Round of the tracked candidate that produced this run — null for a
     *  one-shot run. A round has 1:N evaluations (re-score appends); "current =
     *  latest by createdAt". `set null` on round delete keeps the evaluation. */
    roundId: uuid("round_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentRounds.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_level_assessment_evaluations_model_created").on(table.modelId, table.createdAt),
    index("idx_level_assessment_evaluations_session").on(table.sessionId),
    index("idx_level_assessment_evaluations_set").on(table.evaluationSetId),
    index("idx_level_assessment_evaluations_candidate").on(table.candidateId),
    index("idx_level_assessment_evaluations_round").on(table.roundId),
  ],
);

/** Guided calibration session — the orchestration record behind the
 *  create→calibrate workflow. Groups the reference profiles evaluated and
 *  validated in one round so the workflow can pause and resume on-demand and
 *  (future) reason about input/output coverage.
 *
 *  The per-axis ground truth still flows into the model-global
 *  `level_assessment_axis_samples` corpus (deduped by profile key); a session
 *  only REFERENCES its profiles via `session_id` on the evaluation rows — it
 *  never changes the sample grain, so the GEPA corpus stays shared across
 *  sessions. Cascade-deleted with the model. */
export const levelAssessmentCalibrationSessions = pgTable(
  "level_assessment_calibration_sessions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** Scope being calibrated: null = the whole model (cross + every
     *  discipline); a discipline id = that discipline only (the
     *  "add a discipline" use case). */
    disciplineId: text("discipline_id"),
    /** Provenance for the workflow UI + future profile-coverage analysis:
     *  `new_model` | `new_discipline` | `ongoing`. */
    kind: text("kind").notNull().default("ongoing"),
    /** `collecting` | `evaluating` | `awaiting_validation` | `calibrating` |
     *  `completed` | `abandoned`. The client polls this while a background
     *  step (evaluate / calibrate) runs. */
    status: text("status").notNull().default("collecting"),
    /** Email of the operator/client who started the session. */
    actor: text("actor"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
    completedAt: timestamp("completed_at"),
  },
  (table) => [
    index("idx_level_assessment_calib_sessions_model_created").on(table.modelId, table.createdAt),
  ],
);

// ─── Sourcing (Sondar) — explore-loop (pooled contextual-bandit) ────────────
// Candidate-sourcing labs sub-product. Internal/multi-user (rows carry created_by
// for future org-scoping); see docs/labs/sourcing/.
//
// The bandit reframe: instead of one big blind search, an exploration runs many
// cheap 1-page probes over a facet action-space, learns from recruiter flags,
// and pools that learning across searches per discipline/role scope. Cost
// firewall: only a `(facets,page)` cache MISS spends Apify; a page budget caps
// the spend; empty pages are never stored.

/** Per-(facet-combo, page) Apify result cache — the per-page cost firewall. */
export const sourcingPageCache = pgTable("sourcing_page_cache", {
  /** facetInputSignature(query, selection, page) + PROJECTION_VERSION. */
  signature: text("signature").primaryKey(),
  /** Candidate[] for this single page (pre-rank). Never stored when empty. */
  profiles: jsonb("profiles").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

/** A multi-round bandit exploration session AND its async-job state. */
export const sourcingExplorations = pgTable(
  "sourcing_explorations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    createdBy: text("created_by").notNull(),
    brief: text("brief").notNull(),
    /** StructuredQuery (the editable query the exploration searches over). */
    query: jsonb("query").notNull(),
    /** Pooling scope key (discipline slug / role family) — the posterior bucket. */
    scope: text("scope").notNull(),
    /** Optional LA model/discipline for the prior + LA-as-rater. */
    laModelId: text("la_model_id"),
    laDisciplineId: text("la_discipline_id"),
    /** The level the recruiter targeted from the model's matrix (pins the
     *  enrichment's target level band). Null when no level/model was chosen. */
    laLevel: integer("la_level"),
    /** Flow that produced this row: `explore` (the bandit loop) or `precise` (a
     *  single user-edited faceted search). Lets each flow list/badge only its own
     *  saved searches; precise rows carry `rounds: []` + a single-arm `harvest`. */
    kind: text("kind").notNull().default("explore"),
    /** Owning sourcing project (open position), when the run was started from —
     *  or attached to — one. On completion the run's top-N by FIT auto-ingest
     *  into the project's Sourced block. Null for standalone runs. */
    projectId: uuid("project_id").references(() => sourcingProjects.id, { onDelete: "set null" }),
    /** pending | running | awaiting_input | harvesting | done | error. */
    status: text("status").notNull().default("pending"),
    errorMessage: text("error_message"),
    /** How this run is rewarded: `auto` (LLM judge only — the default) or
     *  `human_in_loop` (each batch pauses to blend the recruiter's ternary ratings
     *  with the judge). Drives which background worker steps the run. */
    ratingMode: text("rating_mode").notNull().default("auto"),
    /** Human-in-the-loop resume snapshot (null for `auto` runs and before the first
     *  pause): the serialized frontier + coverage/loop scalars + the current pause's
     *  pending sample + collected ratings. The pool, pooled model, and explore-value
     *  are NOT stored here — they are rebuilt on resume (pool from `rounds`, the two
     *  posteriors from their durable tables). See explore/run-state.ts for the shape. */
    runState: jsonb("run_state"),
    /** Page budget cap for the whole session (the cost firewall) — derived from
     *  `total_rounds` (rounds' batches + the harvest). */
    budgetPages: integer("budget_pages").notNull().default(16),
    pagesUsed: integer("pages_used").notNull().default(0),
    /** Exploration rounds the user requested (the budget lever) before the harvest. */
    totalRounds: integer("total_rounds").notNull().default(2),
    /** ExplorationRound[] — arms surfaced + flags collected, per round. */
    rounds: jsonb("rounds").notNull().default([]),
    /** HarvestResult — the final exploitation pull (null until the harvest runs). */
    harvest: jsonb("harvest"),
    /** ExplorationDecisionLog — the additive, optional "como exploramos" observability
     *  record: the per-batch full-frontier snapshot (arm rate posteriors + selected/
     *  considered/dormant/revived), the explore-vs-exploit meta decisions, and the real
     *  termination reason. Null on legacy rows and `precise` runs. Written INSIDE the
     *  already-paid loop (no new spend) and — unlike `run_state` — NOT nulled at finalize.
     *  See explore/decision-log.ts for the builder. */
    decisionLog: jsonb("decision_log"),
    /** Denormalized final shortlist size, written when the run finishes — keeps the
     *  history list light (no jsonb scan to show "N candidates found"). */
    resultCount: integer("result_count").notNull().default(0),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_sourcing_explorations_by_user").on(table.createdBy, table.createdAt),
    index("idx_sourcing_explorations_by_project").on(table.projectId, table.createdAt),
  ],
);

/** Append-only recruiter-flag observations — the reward signal feeding the
 *  pooled posterior. Append-then-refit avoids write contention across searches. */
export const sourcingFlagObservations = pgTable(
  "sourcing_flag_observations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    scope: text("scope").notNull(),
    /** φ(query) — the fixed-length abstract facet feature vector (number[]). */
    features: jsonb("features").notNull(),
    /** Bernoulli reward: 1 flagged-up, 0 negative. */
    reward: doublePrecision("reward").notNull(),
    /** Observation weight (<1 for weak/positive-unlabeled negatives) — so a refit
     *  from this append-only log reproduces the PU-weighted posterior exactly. */
    weight: doublePrecision("weight").notNull().default(1),
    /** Where the reward came from: `judge` (the LLM auto-judge, the default and every
     *  legacy row) or `human` (a recruiter's ternary rating on a human-in-the-loop run).
     *  Informational — the fold ignores it — but lets us audit / analyse / later re-weight
     *  the human signal separately. */
    source: text("source").notNull().default("judge"),
    explorationId: uuid("exploration_id").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [index("idx_sourcing_flag_obs_by_scope").on(table.scope, table.createdAt)],
);

/** The pooled per-scope posterior over facet rewards (diagonal-Gaussian over θ). */
export const sourcingFacetPosteriors = pgTable("sourcing_facet_posteriors", {
  scope: text("scope").primaryKey(),
  /** Posterior mean of θ (number[], aligned to featureNames). */
  mean: jsonb("mean").notNull(),
  /** Posterior diagonal variance of θ (number[]). */
  variance: jsonb("variance").notNull(),
  /** Feature names, so a dimension realignment is detectable across versions. */
  featureNames: jsonb("feature_names").notNull(),
  /** Number of observations folded in. */
  count: integer("count").notNull().default(0),
  /** Feature-space version; a bump invalidates an out-of-date posterior. */
  version: integer("version").notNull().default(1),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

/** Per-scope LEARNED explore-value posterior — a Gamma over the FIRST-PULL novel-fit rate
 *  of freshly-injected (LLM-explored) NEW categories. The explore-vs-exploit meta-bandit
 *  Thompson-draws this against the best live arm to decide whether an iteration is better
 *  spent introducing a genuinely new category than exploiting a known vein. Durable +
 *  pooled per scope, like the structural posterior: it learns whether targeted exploration
 *  actually pays off for this kind of role, so the explore threshold is LEARNED, not fixed. */
export const sourcingExploreValue = pgTable("sourcing_explore_value", {
  scope: text("scope").primaryKey(),
  /** Gamma shape over the injected-category first-pull novel-fit rate. */
  alpha: doublePrecision("alpha").notNull(),
  /** Gamma rate. */
  beta: doublePrecision("beta").notNull(),
  /** Exploration episodes folded in (diagnostics / confidence). */
  observations: integer("observations").notNull().default(0),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type SourcingExploreValueRow = typeof sourcingExploreValue.$inferSelect;
export type InsertSourcingExploreValueRow = typeof sourcingExploreValue.$inferInsert;

/** Product-wide Sondar configuration — a SINGLE row (id = 'singleton'). The editable
 *  payload is validated by SourcingConfigSchema (shared/labs/sourcing-config.ts) and
 *  stored as JSONB, so new knobs need no migration. Resolved at run-start and threaded
 *  through the pipeline; an absent/empty row is byte-identical to legacy constants. */
export const sourcingConfig = pgTable("sourcing_config", {
  id: text("id").primaryKey().default("singleton"),
  payload: jsonb("payload").notNull().default({}),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  updatedBy: text("updated_by"),
});
export type SourcingConfigRow = typeof sourcingConfig.$inferSelect;
export type InsertSourcingConfigRow = typeof sourcingConfig.$inferInsert;

/** A sourcing PROJECT — the tracker for ONE open position. Owns the canonical
 *  brief + StructuredQuery every project candidate is judged against (so FIT is
 *  comparable across sessions and manual adds), aggregates the explore/precise
 *  sessions run for the position (each may iterate its own brief), and holds the
 *  persistent candidate pool triaged across Sourced / Shortlisted / Rejected. */
export const sourcingProjects = pgTable(
  "sourcing_projects",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    createdBy: text("created_by").notNull(),
    /** Position name shown on the tracker (e.g. "Backend Sênior — Squad Pagamentos"). */
    name: text("name").notNull(),
    /** Canonical brief (the open position's JD) — the judge's reference text. */
    brief: text("brief").notNull(),
    /** StructuredQuery understood from the brief — what manual adds are judged
     *  against. NULL until first needed: creation stays a free, instant insert
     *  (no LLM), and the query materializes lazily on the first judge that
     *  requires it (or is copied from the founding run). */
    query: jsonb("query"),
    /** Optional LA model/discipline/level scope, carried into sessions + LA-set push. */
    laModelId: text("la_model_id"),
    laDisciplineId: text("la_discipline_id"),
    laLevel: integer("la_level"),
    /** Top-N by FIT auto-ingested into Sourced when a linked run completes. */
    ingestTopN: integer("ingest_top_n").notNull().default(25),
    /** Per-project config: sparse product-config `overrides` (inherited live when
     *  unset) + a `blockedCompanies` list. Default `{}` = inherit product config,
     *  block nothing. Shape: `SourcingProjectConfig` (shared/labs/sourcing-project-config). */
    config: jsonb("config").notNull().default({}),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [index("idx_sourcing_projects_by_user").on(table.createdBy, table.createdAt)],
);
export type SourcingProjectRow = typeof sourcingProjects.$inferSelect;
export type InsertSourcingProjectRow = typeof sourcingProjects.$inferInsert;

/** One candidate inside a sourcing project. Keyed by lowercased LinkedIn
 *  username so later ingests/manual adds dedup against the pool — a candidate
 *  already in the project (any stage) is never re-inserted nor has their stage
 *  or verdict overwritten. Manual adds are written as pending rows first
 *  (`fetch_status` fetching/judging) and filled by a background task the
 *  frontend polls, mirroring the exploration job lifecycle. */
export const sourcingProjectCandidates = pgTable(
  "sourcing_project_candidates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    projectId: uuid("project_id")
      .notNull()
      .references(() => sourcingProjects.id, { onDelete: "cascade" }),
    /** Lowercased LinkedIn username/publicIdentifier — the cross-source dedup key. */
    candidateId: text("candidate_id").notNull(),
    /** Triage stage: sourced | shortlisted | rejected. */
    stage: text("stage").notNull().default("sourced"),
    /** How the candidate entered: exploration | manual-url | manual-pick. */
    source: text("source").notNull(),
    /** The run that surfaced them (exploration/manual-pick sources). */
    sourceExplorationId: uuid("source_exploration_id"),
    /** ScoredCandidate (profile + deterministic signal chips); a URL-only
     *  placeholder while fetch_status = fetching. */
    candidate: jsonb("candidate").notNull(),
    /** CandidateJudgement — null only while the fill is pending; every READY
     *  project candidate carries a verdict (the product invariant). */
    judgement: jsonb("judgement"),
    /** Which brief the verdict was judged against: project | session. */
    judgedAgainst: text("judged_against").notNull(),
    /** ready | fetching | judging | error — the manual-add fill lifecycle. */
    fetchStatus: text("fetch_status").notNull().default("ready"),
    errorMessage: text("error_message"),
    addedBy: text("added_by").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_sourcing_project_candidates_project_candidate").on(
      table.projectId,
      table.candidateId,
    ),
    index("idx_sourcing_project_candidates_by_stage").on(table.projectId, table.stage),
    /** Per-adder spend attribution for the manual-URL (paid) entry path. */
    index("idx_sourcing_project_candidates_by_adder").on(
      table.addedBy,
      table.source,
      table.createdAt,
    ),
  ],
);
export type SourcingProjectCandidateRow = typeof sourcingProjectCandidates.$inferSelect;
export type InsertSourcingProjectCandidateRow = typeof sourcingProjectCandidates.$inferInsert;

/** Self-building company directory — a name→LinkedIn-URL map harvested as a FREE byproduct
 *  of profiles the search already fetched (and cached): every returned profile's work
 *  history carries LinkedIn's own canonical `companyLinkedinUrl` per employer, so we never
 *  guess a slug (LinkedIn's vanity names are arbitrary — "Turing" → `turingcom`). It lets
 *  the explore loop TARGET a company that emerges in the candidate pool but wasn't in the
 *  brief (the literal-company path), resolving the name to the exact URL LinkedIn gave us.
 *  Populated only from pages already paid for — NO new Apify path; pure DB reads/writes off
 *  the money path. `company_id` is LinkedIn's stable numeric id (identity); `name_key` is the
 *  normalized display name used for lookup; `observations` is a soft confidence/ranking count. */
export const sourcingCompanyDirectory = pgTable(
  "sourcing_company_directory",
  {
    /** LinkedIn's stable numeric company id (the identity — display names vary). */
    companyId: text("company_id").primaryKey(),
    /** Normalized display name for case-insensitive lookup by the name the LLM proposes. */
    nameKey: text("name_key").notNull(),
    /** Human display name as last observed (e.g. "Vivo (Telefônica Brasil)"). */
    displayName: text("display_name").notNull(),
    /** LinkedIn's canonical company URL (harvested, authoritative — never slug-guessed). */
    linkedinUrl: text("linkedin_url").notNull(),
    /** How many times this company has been seen across fetched pages (confidence/ranking). */
    observations: integer("observations").notNull().default(1),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [index("idx_sourcing_company_directory_name").on(table.nameKey)],
);
export type SourcingCompanyDirectoryRow = typeof sourcingCompanyDirectory.$inferSelect;
export type InsertSourcingCompanyDirectoryRow = typeof sourcingCompanyDirectory.$inferInsert;

/** Multi-candidate evaluation set — a user-named container of up to 20 candidate
 *  evaluations run against ONE model + (optional) discipline. This is the
 *  client-facing "pacote de candidatos": a hiring artifact, deliberately DISTINCT
 *  from a calibration session (which is model-training orchestration with its own
 *  GEPA/readiness semantics). A set REFERENCES its evaluations via
 *  `evaluation_set_id` on the evaluation rows — it never changes the axis-sample
 *  grain. It carries its own minimal batch lifecycle (`evaluating` → `ready`,
 *  plus a manual `abandoned`) flipped by the background batch runner and reaped if
 *  a process recycle orphans the run. Cascade-deleted with the model. */
export const levelAssessmentEvaluationSets = pgTable(
  "level_assessment_evaluation_sets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** Discipline scored for every candidate in the set — null = cross-only model. */
    disciplineId: text("discipline_id"),
    /** Required, user-chosen name — the client-facing artifact label. */
    name: text("name").notNull(),
    /** Optional free-text reference/observação (e.g. a req id like "#FD-204"). */
    note: text("note"),
    /** `evaluating` | `ready` | `abandoned`. The client polls this while the
     *  background batch runs; the reaper flips a stale `evaluating` set to `ready`. */
    status: text("status").notNull().default("evaluating"),
    /** Profiles accepted into the batch — the N/M progress denominator. */
    expectedCount: integer("expected_count").notNull().default(0),
    /** Email of the actor who created the set (or null). */
    createdBy: text("created_by"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_level_assessment_eval_sets_model_created").on(table.modelId, table.createdAt),
  ],
);

/** A tracked candidate — the stable identity behind multi-round, evolutionary
 *  evaluation. Unlike a one-shot evaluation (which is a self-contained run with
 *  no continuity), a candidate persists across rounds and OWNS the timeline of
 *  rounds + information packets. This is deliberately NOT an ATS entity: there
 *  are no pipelines, stages, or hiring workflow — only an evidence-accumulation
 *  timeline for one person's leveling.
 *
 *  The model is PINNED here (`model_id` + optional `discipline_id`) so every
 *  round is comparable against one model. Rounds reference the candidate via
 *  `candidate_id`; the accumulated evidence for a round is every packet whose
 *  round's `sequence` is ≤ that round's. Cascade-deleted with the model. */
export const levelAssessmentCandidates = pgTable(
  "level_assessment_candidates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /** Pinned model — all of this candidate's rounds score against it. */
    modelId: text("model_id")
      .notNull()
      .references(() => levelAssessmentModels.id, { onDelete: "cascade" }),
    /** Pinned discipline — null for a cross-only model. Plain text (disciplines
     *  live inside the model blob), validated at the usecase boundary. */
    disciplineId: text("discipline_id"),
    /** Display name of the candidate. */
    name: text("name").notNull(),
    /** Optional subtitle (e.g. a LinkedIn headline) shown alongside the name. */
    subtitle: text("subtitle"),
    /** `active` | `archived`. Candidate-level lifecycle only — the async
     *  per-run lifecycle lives on the round, never here. */
    status: text("status").notNull().default("active"),
    /** Email of the actor who created the candidate (or null). */
    createdBy: text("created_by"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_level_assessment_candidates_model_created").on(table.modelId, table.createdAt),
  ],
);

/** One evaluative iteration of a candidate — a "round" of screening. Ordered by
 *  `sequence` within the candidate (1, 2, 3…). A round carries the async batch
 *  lifecycle (`evaluating` → `ready`, plus a manual `abandoned`) — the structural
 *  twin of an evaluation set's status — flipped by the background round runner
 *  and reaped if a process recycle orphans the run.
 *
 *  A round has 1:N evaluations: normally one, but a re-score APPENDS a new
 *  evaluation row (evaluations are immutable), so "current = latest by
 *  created_at", exactly as sessions/sets resolve current. The link lives on the
 *  evaluation row (`round_id`). Cascade-deleted with the candidate. */
export const levelAssessmentRounds = pgTable(
  "level_assessment_rounds",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    candidateId: uuid("candidate_id")
      .notNull()
      .references(() => levelAssessmentCandidates.id, { onDelete: "cascade" }),
    /** 1-based position within the candidate. Unique per candidate; assigned as
     *  max+1 in a transaction when a round is added. Also the accumulation
     *  anchor: a round evaluates over every packet added at a round with
     *  `sequence` ≤ this one. */
    sequence: integer("sequence").notNull(),
    /** Optional human label for the round (e.g. "Triagem CV", "Entrevista técnica"). */
    label: text("label"),
    /** `evaluating` | `ready` | `abandoned`. The client polls this while the
     *  background run computes; the reaper flips a stale `evaluating` round to
     *  `ready`. */
    status: text("status").notNull().default("evaluating"),
    /** Always 1 — a round yields exactly one evaluation regardless of how many
     *  packets it accumulated. Kept for parity with the set's N/M progress, but
     *  never tied to packet count (that would make a multi-packet round hang). */
    expectedCount: integer("expected_count").notNull().default(1),
    /** `legacy` | `fusion` — which scoring path produced this round, stamped
     *  at insert. `fusion` for rounds created after the multi-round update
     *  model shipped; `legacy` (the default) for every pre-existing row, so
     *  outcome derivation can branch safely without reinterpreting history. */
    scoringMode: text("scoring_mode").notNull().default("legacy"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_level_assessment_rounds_candidate_sequence").on(table.candidateId, table.sequence),
    unique("uq_level_assessment_rounds_candidate_sequence").on(table.candidateId, table.sequence),
  ],
);

/** A first-class evidence artifact attached to a candidate — the "information
 *  packet" that entered the case at a given round. This is what turns leveling
 *  into an evolutionary process: instead of a single profile snapshot inline on
 *  one run, evidence accumulates as timestamped, typed, round-attributed packets.
 *
 *  `added_at_round_id` records the round the evidence ENTERED at (its provenance
 *  anchor); it does not cascade off the round (rounds only die via the candidate
 *  cascade). Packets ACCUMULATE: a round scores over every packet whose round's
 *  `sequence` is ≤ its own. Cascade-deleted with the candidate. */
export const levelAssessmentInformationPackets = pgTable(
  "level_assessment_information_packets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    candidateId: uuid("candidate_id")
      .notNull()
      .references(() => levelAssessmentCandidates.id, { onDelete: "cascade" }),
    /** The round at which this evidence first entered the case — provenance. */
    addedAtRoundId: uuid("added_at_round_id")
      .notNull()
      .references(() => levelAssessmentRounds.id, { onDelete: "restrict" }),
    /** `linkedin` | `cv` | `interview_transcript` | `free_text`. */
    type: text("type").notNull(),
    /** Human label for the packet (e.g. "CV 2024", "Entrevista técnica – João"). */
    label: text("label").notNull(),
    /** LinkedIn profile URL — set only for `linkedin` packets. */
    linkedinUrl: text("linkedin_url"),
    /** Apify snapshot — PII. Set only for `linkedin` packets. */
    rawProfile: jsonb("raw_profile"),
    /** Pasted text — set for `cv` | `interview_transcript` | `free_text` packets. */
    content: text("content"),
    /** sha256 of the packet payload — dedupe within a candidate. */
    contentHash: text("content_hash"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_level_assessment_info_packets_candidate").on(table.candidateId),
    index("idx_level_assessment_info_packets_round").on(table.addedAtRoundId),
  ],
);

/**
 * Interview panels — a per-ROUND interviewer briefing synthesized from the
 * round's effective evaluation (a read/synthesis layer; never re-scores). Keyed
 * by `round_id` (unique, one panel per round) rather than by evaluation:
 * two consecutive carry-forward rounds share the same effective evaluation but
 * accumulate different evidence, so their panels differ. Lazy-generated on first
 * open + cached; the async lifecycle (`generating → ready`, reaped if orphaned)
 * mirrors the rounds/sets twin. Invalidated when the effective evaluation
 * (`source_evaluation_id`), the accumulated packet set (`packet_fingerprint`),
 * or the payload version (`panel_version`) changes. Cascade-deleted with the
 * round or candidate; `source_evaluation_id` is `set null` (a shared `/e/:id`
 * evaluation survives a candidate/round delete).
 */
export const levelAssessmentInterviewPanels = pgTable(
  "level_assessment_interview_panels",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    roundId: uuid("round_id")
      .notNull()
      .references(() => levelAssessmentRounds.id, { onDelete: "cascade" }),
    candidateId: uuid("candidate_id")
      .notNull()
      .references(() => levelAssessmentCandidates.id, { onDelete: "cascade" }),
    /** The EFFECTIVE evaluation the panel was built from — the round's own eval,
     *  or the one carried forward for a text-only round. Nullable: a round with
     *  no scored evaluation yet has no source. Part of the cache key. */
    sourceEvaluationId: uuid("source_evaluation_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentEvaluations.id,
      { onDelete: "set null" },
    ),
    /** Hash of the accumulated packet set (`sequence ≤ N`) — busts the cache
     *  when new evidence enters at or below this round. */
    packetFingerprint: text("packet_fingerprint").notNull().default(""),
    /** The assembled `InterviewPanel` payload — null while `generating`/`error`. */
    panel: jsonb("panel"),
    /** `generating` | `ready` | `error`. The client polls this while the
     *  background assembly runs; the reaper flips a stale `generating` panel to
     *  `error`. */
    status: text("status").notNull().default("generating"),
    /** Payload schema/prompt version (mirrors `INTERVIEW_PANEL_VERSION`). A bump
     *  forces regeneration on next open. Part of the cache key. */
    panelVersion: integer("panel_version").notNull().default(1),
    error: text("error"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    unique("uq_level_assessment_interview_panels_round").on(table.roundId),
    index("idx_level_assessment_interview_panels_candidate").on(table.candidateId),
  ],
);

/**
 * Round updates — the fusion-phase traceability artifact of ONE round: the
 * evidence claims extracted, what happened to each packet, the open-points
 * ledger state after the round, and (when the round re-scored) the movement
 * record. One row per round (unique `round_id`, guarded upsert — the latest
 * complete write wins, mirroring the interview panel's CAS grammar). Round 1
 * gets a row too (bootstrap ledger, `movement` null). `evaluation_id` is null
 * for a `no_new_signal` round (claims recorded, nothing routed). Cascade-
 * deleted with the round or candidate; `evaluation_id` is `set null` (a
 * shared `/e/:id` evaluation survives a candidate/round delete). See
 * `shared/labs/level-assessment-round-update.ts` for the parsed contract and
 * `docs/labs/level-assessment/multi-round-update-model.md` §8.
 */
export const levelAssessmentRoundUpdates = pgTable(
  "level_assessment_round_updates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    candidateId: uuid("candidate_id")
      .notNull()
      .references(() => levelAssessmentCandidates.id, { onDelete: "cascade" }),
    roundId: uuid("round_id")
      .notNull()
      .references(() => levelAssessmentRounds.id, { onDelete: "cascade" }),
    /** The evaluation this round's update produced — null when the round
     *  produced no new evaluation (`no_new_signal`, or round 1's bootstrap). */
    evaluationId: uuid("evaluation_id").references(
      (): import("drizzle-orm/pg-core").AnyPgColumn => levelAssessmentEvaluations.id,
      { onDelete: "set null" },
    ),
    /** `EvidenceClaim[]` extracted this round. */
    claims: jsonb("claims").notNull().default([]),
    /** `PacketOutcome[]` — per-packet extraction outcome this round. */
    packetOutcomes: jsonb("packet_outcomes").notNull().default([]),
    /** `OpenPoint[]` — the full open-point state AFTER this round. */
    ledger: jsonb("ledger").notNull().default([]),
    /** `RoundMovement | null` — present only when the round re-scored. */
    movement: jsonb("movement"),
    /** `RoundSaturation | null` (v2) — the round's settledness/stop-signal
     *  read; null on v1 rows and rounds that produced no assessment. */
    saturation: jsonb("saturation"),
    /** Contract version (mirrors `ROUND_UPDATE_VERSION`). */
    version: integer("version").notNull().default(1),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    unique("uq_level_assessment_round_updates_round").on(table.roundId),
    index("idx_level_assessment_round_updates_candidate").on(table.candidateId),
  ],
);

// Drizzle relations — typed joins.
export const levelAssessmentModelsRelations = relations(levelAssessmentModels, ({ many }) => ({
  disciplines: many(levelAssessmentModelDisciplines),
  rubrics: many(levelAssessmentModelRubrics),
  prompts: many(levelAssessmentModelPrompts),
  axisSamples: many(levelAssessmentAxisSamples),
  axisCalibrationRuns: many(levelAssessmentAxisCalibrationRuns),
  evaluations: many(levelAssessmentEvaluations),
  calibrationSessions: many(levelAssessmentCalibrationSessions),
  evaluationSets: many(levelAssessmentEvaluationSets),
  candidates: many(levelAssessmentCandidates),
}));
export const levelAssessmentModelDisciplinesRelations = relations(
  levelAssessmentModelDisciplines,
  ({ one }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentModelDisciplines.modelId],
      references: [levelAssessmentModels.id],
    }),
  }),
);
export const levelAssessmentModelRubricsRelations = relations(
  levelAssessmentModelRubrics,
  ({ one }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentModelRubrics.modelId],
      references: [levelAssessmentModels.id],
    }),
  }),
);
export const levelAssessmentModelPromptsRelations = relations(
  levelAssessmentModelPrompts,
  ({ one }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentModelPrompts.modelId],
      references: [levelAssessmentModels.id],
    }),
  }),
);
export const levelAssessmentAxisSamplesRelations = relations(
  levelAssessmentAxisSamples,
  ({ one }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentAxisSamples.modelId],
      references: [levelAssessmentModels.id],
    }),
  }),
);
export const levelAssessmentAxisCalibrationRunsRelations = relations(
  levelAssessmentAxisCalibrationRuns,
  ({ one }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentAxisCalibrationRuns.modelId],
      references: [levelAssessmentModels.id],
    }),
  }),
);
export const levelAssessmentEvaluationsRelations = relations(
  levelAssessmentEvaluations,
  ({ one }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentEvaluations.modelId],
      references: [levelAssessmentModels.id],
    }),
    session: one(levelAssessmentCalibrationSessions, {
      fields: [levelAssessmentEvaluations.sessionId],
      references: [levelAssessmentCalibrationSessions.id],
    }),
    evaluationSet: one(levelAssessmentEvaluationSets, {
      fields: [levelAssessmentEvaluations.evaluationSetId],
      references: [levelAssessmentEvaluationSets.id],
    }),
    candidate: one(levelAssessmentCandidates, {
      fields: [levelAssessmentEvaluations.candidateId],
      references: [levelAssessmentCandidates.id],
    }),
    round: one(levelAssessmentRounds, {
      fields: [levelAssessmentEvaluations.roundId],
      references: [levelAssessmentRounds.id],
    }),
  }),
);
export const levelAssessmentCalibrationSessionsRelations = relations(
  levelAssessmentCalibrationSessions,
  ({ one, many }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentCalibrationSessions.modelId],
      references: [levelAssessmentModels.id],
    }),
    evaluations: many(levelAssessmentEvaluations),
  }),
);
export const levelAssessmentEvaluationSetsRelations = relations(
  levelAssessmentEvaluationSets,
  ({ one, many }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentEvaluationSets.modelId],
      references: [levelAssessmentModels.id],
    }),
    evaluations: many(levelAssessmentEvaluations),
  }),
);
export const levelAssessmentCandidatesRelations = relations(
  levelAssessmentCandidates,
  ({ one, many }) => ({
    model: one(levelAssessmentModels, {
      fields: [levelAssessmentCandidates.modelId],
      references: [levelAssessmentModels.id],
    }),
    rounds: many(levelAssessmentRounds),
    packets: many(levelAssessmentInformationPackets),
    evaluations: many(levelAssessmentEvaluations),
  }),
);
export const levelAssessmentRoundsRelations = relations(levelAssessmentRounds, ({ one, many }) => ({
  candidate: one(levelAssessmentCandidates, {
    fields: [levelAssessmentRounds.candidateId],
    references: [levelAssessmentCandidates.id],
  }),
  packetsAdded: many(levelAssessmentInformationPackets),
  evaluations: many(levelAssessmentEvaluations),
}));
export const levelAssessmentInformationPacketsRelations = relations(
  levelAssessmentInformationPackets,
  ({ one }) => ({
    candidate: one(levelAssessmentCandidates, {
      fields: [levelAssessmentInformationPackets.candidateId],
      references: [levelAssessmentCandidates.id],
    }),
    addedAtRound: one(levelAssessmentRounds, {
      fields: [levelAssessmentInformationPackets.addedAtRoundId],
      references: [levelAssessmentRounds.id],
    }),
  }),
);

export const levelAssessmentInterviewPanelsRelations = relations(
  levelAssessmentInterviewPanels,
  ({ one }) => ({
    round: one(levelAssessmentRounds, {
      fields: [levelAssessmentInterviewPanels.roundId],
      references: [levelAssessmentRounds.id],
    }),
    candidate: one(levelAssessmentCandidates, {
      fields: [levelAssessmentInterviewPanels.candidateId],
      references: [levelAssessmentCandidates.id],
    }),
    sourceEvaluation: one(levelAssessmentEvaluations, {
      fields: [levelAssessmentInterviewPanels.sourceEvaluationId],
      references: [levelAssessmentEvaluations.id],
    }),
  }),
);

export const levelAssessmentRoundUpdatesRelations = relations(
  levelAssessmentRoundUpdates,
  ({ one }) => ({
    round: one(levelAssessmentRounds, {
      fields: [levelAssessmentRoundUpdates.roundId],
      references: [levelAssessmentRounds.id],
    }),
    candidate: one(levelAssessmentCandidates, {
      fields: [levelAssessmentRoundUpdates.candidateId],
      references: [levelAssessmentCandidates.id],
    }),
    evaluation: one(levelAssessmentEvaluations, {
      fields: [levelAssessmentRoundUpdates.evaluationId],
      references: [levelAssessmentEvaluations.id],
    }),
  }),
);

// Row types.
export type LevelAssessmentModelRow = typeof levelAssessmentModels.$inferSelect;
export type InsertLevelAssessmentModelRow = typeof levelAssessmentModels.$inferInsert;
export type LevelAssessmentModelDisciplineRow = typeof levelAssessmentModelDisciplines.$inferSelect;
export type InsertLevelAssessmentModelDisciplineRow =
  typeof levelAssessmentModelDisciplines.$inferInsert;
export type LevelAssessmentModelRubricRow = typeof levelAssessmentModelRubrics.$inferSelect;
export type InsertLevelAssessmentModelRubricRow = typeof levelAssessmentModelRubrics.$inferInsert;
export type LevelAssessmentModelPromptRow = typeof levelAssessmentModelPrompts.$inferSelect;
export type InsertLevelAssessmentModelPromptRow = typeof levelAssessmentModelPrompts.$inferInsert;
export type LevelAssessmentAxisSampleRow = typeof levelAssessmentAxisSamples.$inferSelect;
export type InsertLevelAssessmentAxisSampleRow = typeof levelAssessmentAxisSamples.$inferInsert;
export type LevelAssessmentAxisCalibrationRunRow =
  typeof levelAssessmentAxisCalibrationRuns.$inferSelect;
export type InsertLevelAssessmentAxisCalibrationRunRow =
  typeof levelAssessmentAxisCalibrationRuns.$inferInsert;
export type LevelAssessmentEvaluationRow = typeof levelAssessmentEvaluations.$inferSelect;
export type InsertLevelAssessmentEvaluationRow = typeof levelAssessmentEvaluations.$inferInsert;
export type LevelAssessmentCalibrationSessionRow =
  typeof levelAssessmentCalibrationSessions.$inferSelect;
export type InsertLevelAssessmentCalibrationSessionRow =
  typeof levelAssessmentCalibrationSessions.$inferInsert;
export type LevelAssessmentEvaluationSetRow = typeof levelAssessmentEvaluationSets.$inferSelect;
export type InsertLevelAssessmentEvaluationSetRow =
  typeof levelAssessmentEvaluationSets.$inferInsert;
export type LevelAssessmentCandidateRow = typeof levelAssessmentCandidates.$inferSelect;
export type InsertLevelAssessmentCandidateRow = typeof levelAssessmentCandidates.$inferInsert;
export type LevelAssessmentRoundRow = typeof levelAssessmentRounds.$inferSelect;
export type InsertLevelAssessmentRoundRow = typeof levelAssessmentRounds.$inferInsert;
export type LevelAssessmentInformationPacketRow =
  typeof levelAssessmentInformationPackets.$inferSelect;
export type InsertLevelAssessmentInformationPacketRow =
  typeof levelAssessmentInformationPackets.$inferInsert;
export type LevelAssessmentInterviewPanelRow = typeof levelAssessmentInterviewPanels.$inferSelect;
export type InsertLevelAssessmentInterviewPanelRow =
  typeof levelAssessmentInterviewPanels.$inferInsert;
export type LevelAssessmentRoundUpdateRow = typeof levelAssessmentRoundUpdates.$inferSelect;
export type InsertLevelAssessmentRoundUpdateRow = typeof levelAssessmentRoundUpdates.$inferInsert;

export type SourcingPageCacheRow = typeof sourcingPageCache.$inferSelect;
export type InsertSourcingPageCacheRow = typeof sourcingPageCache.$inferInsert;
export type SourcingExplorationRow = typeof sourcingExplorations.$inferSelect;
export type InsertSourcingExplorationRow = typeof sourcingExplorations.$inferInsert;
export type SourcingFlagObservationRow = typeof sourcingFlagObservations.$inferSelect;
export type InsertSourcingFlagObservationRow = typeof sourcingFlagObservations.$inferInsert;
export type SourcingFacetPosteriorRow = typeof sourcingFacetPosteriors.$inferSelect;
export type InsertSourcingFacetPosteriorRow = typeof sourcingFacetPosteriors.$inferInsert;

// ==================== USER FIREFLIES CREDENTIALS ====================
// A user's PERSONAL Fireflies API key, used to read their own host-private
// ("Only Owner") meetings that the shared org key can't discover. The key is
// stored AES-256-GCM encrypted (never in plaintext) and scoped one-per-user.
// Transcripts fetched with it are returned live and cached on the interview
// row only — they are NEVER pushed to the shared org context store.
export const userFirefliesCredentials = pgTable("user_fireflies_credentials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .unique()
    .references(() => users.id, { onDelete: "cascade" }),
  encryptedApiKey: text("encrypted_api_key").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type UserFirefliesCredential = typeof userFirefliesCredentials.$inferSelect;
export type InsertUserFirefliesCredential = typeof userFirefliesCredentials.$inferInsert;
